import {
  ClinicalDataStatus,
  DateField,
  MoonFilterChip,
  MoonSearchInput,
  MoonToggle,
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
  SecondaryButton,
  CompactActionButton,
  Row,
  SectionTitle,
  Tag,
  BackLink,
  ListRowSkeleton,
  EmptyState,
  SupportingSubtitleText,
} from "@louise/moon-ui";
import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  Alert,
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import * as Clipboard from "expo-clipboard";
import type { BottomTabNavigationProp } from "@react-navigation/bottom-tabs";
import {
  BadgePercent,
  Building2,
  Camera,
  ChevronRight,
  Check,
  ExternalLink,
  FileText,
  HeartHandshake,
  Layers,
  Mail,
  Phone,
  Pill,
  Scale,
  Syringe,
  Ticket,
  DollarSign,
  Shield,
  Sparkles,
  Store,
  Truck,
  RefreshCw,
  UploadCloud,
  UserCheck,
  ListChecks,
} from "lucide-react-native";
import { SvgUri } from "react-native-svg";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Asset } from "expo-asset";
import { getTodayString } from "@src/packages/utils/date";
import {
  readFileForUpload,
  resolveUploadContentType,
} from "@src/packages/utils/fileUpload";
import {
  useCompleteUpload,
  useCreateDocument,
  useDocuments,
  useGetDownloadUrl,
  useGetUploadUrl,
  useUploadFile,
  type Document as PatientDocument,
} from "@src/packages/documents/hooks/useDocuments";
import { getMedicationByBrandName } from "@src/packages/content";
import type { ContentBlock } from "@src/packages/content";
import type { HomepageMedicationFactSheetPayload } from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import {
  MOCK_PHARMACY_MEDICATIONS,
  MOCK_PHARMACY_QUOTES,
  PHARMACY_SAVINGS_PROGRAMS,
  localizePharmacySavingsProgram,
  usePharmacyDirectory,
  type PharmacyDemoQuote,
  type PharmacySavingsProgram,
  type PharmacyDirectoryRecord,
  type PharmacyDirectoryType,
} from "@src/packages/providers";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList, MainTabParamList } from "../../types";
import { pharmacyLogoImages } from "./pharmacyLogos";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import MedicationFactsheetSheet from "../../components/MedicationFactsheetSheet";
import type { MedicationUserContext } from "../../components/MedicationFactsheetSheet";
import CostsCardTabs from "../../components/CostsCardTabs";
import { useTranslations, useLocale } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import PharmacyHeader from "../../components/access/PharmacyHeader";
import { ProgramDetailsDialog } from "../../components/access/ProgramDetailsDialog";
import {
  pharmacyFulfillmentCards,
  PharmacyActionCard,
  PharmacyFulfillmentCard as DesignedPharmacyFulfillmentCard,
} from "../care/suggested/CareReviewConceptScreen";
import { semanticColors, colors, withAlpha } from "@louise/theme/tokens";
import {
  PharmacyPrescriptionSection,
  type ConciergePrescriptionFile,
  type ExtractedMedication,
  type QuoteRequestStatus,
} from "./PharmacyPrescriptionSection";
import {
  PharmacyActionButton,
  ScanTileButton,
} from "./pharmacyConciergeShared";
import {
  PharmacyConciergeQuoteSheet,
  type ConciergeQuoteFormValues,
} from "./PharmacyConciergeQuoteSheet";
import { useDocumentSourceTransfer } from "@src/packages/documents/ingestion/useDocumentSourceTransfer";
import { projectDocumentProcessingRunToPrefill } from "@src/packages/documents/ingestion/documentProcessingRun";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  useCancelPharmacyQuoteRequest,
  useCreatePharmacyQuoteRequestDraft,
  useCurrentPharmacyQuoteRequest,
  useSubmitPharmacyQuoteRequest,
  useUpdatePharmacyQuoteRequestDraft,
  type PharmacyQuoteRequest,
  type PharmacyQuoteSubmission,
} from "@src/packages/providers/hooks/usePharmacyQuoteRequest";
import { getApiErrorMessage } from "@src/packages/api/api-error";
import type { UploadedDocumentSource } from "@src/packages/documents/ingestion/useDocumentSourceTransfer";
import { useFocusEffect } from "@react-navigation/native";
import Constants from "expo-constants";
import {
  PharmacyConciergeConfirmModal,
  type ConfirmDialogVariant,
} from "./PharmacyConciergeConfirmModal";

type Props = NativeStackScreenProps<AccessStackParamList, "Pharmacy">;
type MedicationForm = "pen" | "vial" | "syringe" | "tablet" | "suppository";
type DisplayMedication = {
  id: string;
  name: string;
  dosage: string;
  form: MedicationForm;
  route?: string;
  coverage?: "covered" | "cash_pay";
};

type MedicationStorageProfile = {
  statusKey: string;
  statusTone: "blue" | "green" | "neutral";
  windowKey: string;
  detailKey: string;
};

type PharmacyTab = "prescription" | "quotes" | "savings";

// The Rx the patient just picked, until the backend `PharmacyQuoteRequest`
// draft exists for it. Once the draft is created the request (loaded via
// `GET .../pharmacy-quote-requests/current`) is the only source of truth for
// the file name, detected meds and status.
type PendingPrescription = {
  file: ConciergePrescriptionFile;
  medications: ExtractedMedication[];
  clinicName: string;
  prescriberName: string;
  /** Rx date read off the document (ISO date), pre-fills the cycle start. */
  startDate: string;
  extractionFailed: boolean;
};

function toQuoteRequestStatus(
  request: PharmacyQuoteRequest | null,
): QuoteRequestStatus {
  switch (request?.status) {
    case "submitted":
      return "waiting";
    case "done":
      return "done";
    default:
      return "idle";
  }
}

// DateField emits ISO YYYY-MM-DD, but profile dates may carry a time part
// and the backend snapshots are strict `date` fields.
function toIsoDate(value: string): string | null {
  const match = /^\d{4}-\d{2}-\d{2}/.exec(value);
  return match ? match[0] : null;
}

// The pharmacies Louise's concierge currently dispatches to. MDR is
// intentionally excluded until Louise has a controlled mobile line (MDR only
// returns quotes by SMS). Order here is the order shown in the section UI.
const CONCIERGE_TARGET_PHARMACIES: {
  id: string;
  name: string;
  /** Case-insensitive keyword to match against `PharmacyPatientQuote.pharmacyName`
   *  when the patient uploads a quote so we can pair it back to this dispatch. */
  matchKey: string;
}[] = [
  {
    id: "org-pharmacy-rosemont",
    name: "Rosemont Pharmacy",
    matchKey: "rosemont",
  },
  {
    id: "org-pharmacy-apri",
    name: "APRI Fertility Pharmacy",
    matchKey: "apri",
  },
  { id: "org-pharmacy-apthorp", name: "Apthorp Pharmacy", matchKey: "apthorp" },
  {
    id: "org-pharmacy-mandells",
    name: "Mandell's Pharmacy",
    matchKey: "mandell",
  },
  { id: "org-pharmacy-metro-drugs", name: "Metro Drugs", matchKey: "metro" },
];

// Version of `access.pharmacy.concierge_quote_consent` sent with the request.
// The backend pins the versions it accepts and stores the rendered text next
// to it, so a wording change must bump this together with the copy.
const CONCIERGE_CONSENT_VERSION = "2026-09-17-v1";

// Free-text pharmacy names on uploaded quotes get normalised then matched to a
// dispatch by keyword. "Rosemont Pharmacy" typed on the quote resolves back to
// the "org-pharmacy-rosemont" dispatch even if the casing/spacing differs.
function matchDispatchIdFromPharmacyName(name: string): string | null {
  const norm = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  for (const pharmacy of CONCIERGE_TARGET_PHARMACIES) {
    if (norm.includes(pharmacy.matchKey)) return pharmacy.id;
  }
  return null;
}

type PharmacyPatientQuote = {
  id: string;
  pharmacyName: string;
  totalPrice: number | null;
  quoteDate: string;
  note: string;
  source: "manual" | "uploaded";
  documentName?: string;
  uploadId?: string | null;
  documentId?: string | null;
};

const PHARMACY_QUOTE_TAG = "pharmacy-quotes";
const PHARMACY_QUOTE_PATIENT_TAG = "patient-provided";
const PHARMACY_QUOTE_PHARMACY_TAG = "quote-pharmacy:";
const PHARMACY_QUOTE_TOTAL_TAG = "quote-total-cents:";
const PHARMACY_QUOTE_NOTE_TAG = "quote-note:";

function encodePharmacyQuoteTag(prefix: string, value: string) {
  return `${prefix}${encodeURIComponent(value)}`;
}

function decodePharmacyQuoteTag(tag: string, prefix: string) {
  if (!tag.startsWith(prefix)) return null;

  try {
    return decodeURIComponent(tag.slice(prefix.length));
  } catch {
    return null;
  }
}

function pharmacyQuoteFromDocument(
  document: PatientDocument,
): PharmacyPatientQuote | null {
  if (
    !document.tags.includes(PHARMACY_QUOTE_TAG) ||
    !document.tags.includes(PHARMACY_QUOTE_PATIENT_TAG)
  ) {
    return null;
  }

  const pharmacyTag = document.tags.find((tag) =>
    tag.startsWith(PHARMACY_QUOTE_PHARMACY_TAG),
  );
  const totalTag = document.tags.find((tag) =>
    tag.startsWith(PHARMACY_QUOTE_TOTAL_TAG),
  );
  const noteTag = document.tags.find((tag) =>
    tag.startsWith(PHARMACY_QUOTE_NOTE_TAG),
  );
  const totalCents = totalTag
    ? Number(totalTag.slice(PHARMACY_QUOTE_TOTAL_TAG.length))
    : NaN;
  const upload = document.uploads.find(
    (candidate) => candidate.uploadStatus !== "pending",
  );

  return {
    id: document.id,
    pharmacyName:
      (pharmacyTag
        ? decodePharmacyQuoteTag(pharmacyTag, PHARMACY_QUOTE_PHARMACY_TAG)
        : null) ?? document.name.replace(/^Pharmacy quote – /, ""),
    totalPrice:
      Number.isFinite(totalCents) && totalCents > 0 ? totalCents / 100 : null,
    quoteDate: document.documentDate ?? document.createdAt ?? getTodayString(),
    note: noteTag
      ? (decodePharmacyQuoteTag(noteTag, PHARMACY_QUOTE_NOTE_TAG) ?? "")
      : "",
    source: upload ? "uploaded" : "manual",
    documentName: upload?.name ?? document.name,
    uploadId: upload?.id ?? null,
  };
}

type SelectedPharmacyQuote =
  | { kind: "patient"; quote: PharmacyPatientQuote }
  | {
      kind: "demo";
      quote: PharmacyDemoQuote;
      pharmacy: PharmacyDirectoryRecord;
    };

type PharmacyQuoteFile = {
  source: "camera" | "document";
  name: string;
  uri: string;
  mimeType: string;
  uploadId?: string;
  documentId?: string;
};

type MedicationHelpTopicId =
  | "storage"
  | "injection_disposal"
  | "missed_damaged"
  | "delivery_refills"
  | "travel";

type MedicationHelpTopic = {
  id: MedicationHelpTopicId;
  illustration: number;
  titleKey: string;
  summaryKey: string;
  bodyKey: string;
  nextStepKey: string;
  sourceUrl: string;
};

const pillCapsuleImage =
  require("../../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../../assets/images/injection-pen.png") as number;
const costEstimateCoinsImage =
  require("../../../assets/images/cost-estimate-coins.png") as number;
const trumpRxCouponEagleImage =
  require("../../../assets/images/trumprx/coupon-eagle.webp") as number;
const trumpRxCouponTextureAsset = Asset.fromModule(
  require("../../../assets/images/trumprx/coupon-texture.svg"),
);
const medicationHelpIllustrations: Record<MedicationHelpTopicId, number> = {
  storage:
    require("../../../assets/images/pharmacy-help/storage.png") as number,
  injection_disposal:
    require("../../../assets/images/pharmacy-help/injection-disposal.png") as number,
  missed_damaged:
    require("../../../assets/images/pharmacy-help/missed-damaged.png") as number,
  delivery_refills:
    require("../../../assets/images/pharmacy-help/delivery-refills.png") as number,
  travel: require("../../../assets/images/pharmacy-help/travel.png") as number,
};
const savingsProgramLogoImages: Record<string, number> = {
  "emd-fertility-instant-savings":
    require("../../../assets/images/savings-programs/emd-fertility.png") as number,
  "emd-compassionate-care":
    require("../../../assets/images/savings-programs/emd-fertility.png") as number,
  "emd-compassionate-corps":
    require("../../../assets/images/savings-programs/emd-fertility.png") as number,
  "reunite-rx":
    require("../../../assets/images/savings-programs/reunite-rx.png") as number,
  "reunite-assist":
    require("../../../assets/images/savings-programs/reunite-rx.png") as number,
  "reunite-oncofertility":
    require("../../../assets/images/savings-programs/reunite-rx.png") as number,
  "livestrong-fertility":
    require("../../../assets/images/savings-programs/livestrong.png") as number,
};
const savingsProgramLogoAssets: Record<string, Asset[]> = {
  "inside-rx-fertility": [
    Asset.fromModule(
      require("../../../assets/images/savings-programs/inside-rx.svg"),
    ),
  ],
};

const BROWN = colors.greyBrown;
const BROWN_BG = withAlpha(colors.greyBrown, 0.1);
const TAG_BORDER = semanticColors.border.strong;

const PHARMACY_TAG_LABELS: Record<string, string> = {
  "same-day-delivery": "Same-day delivery",
  "next-day-delivery": "Next-day delivery",
  "overnight-delivery": "Overnight delivery",
  "overnight-shipping": "Overnight shipping",
  "injection-training": "Injection training",
  "cold-chain": "Cold chain",
  "cash-pay": "Cash pay",
  "price-match": "Price match",
  "automatic-discounts": "Automatic discounts",
  "mail-order": "Mail order",
  "auto-refill": "Auto-refill",
  "in-store-pickup": "Pickup",
  "trumprx-participating": "TrumpRx",
  retail: "Retail",
};

const PHARMACY_TAG_ICONS: Record<string, LucideIcon> = {
  "injection-training": Syringe,
  "automatic-discounts": BadgePercent,
  "cash-pay": DollarSign,
  "price-match": Scale,
  "auto-refill": RefreshCw,
  "trumprx-participating": Ticket,
};

const PHARMACY_TAG_STYLE = {
  paddingHorizontal: 8,
  paddingVertical: 4,
  borderRadius: 8,
  backgroundColor: semanticColors.action.quiet.background,
  borderWidth: 1,
  borderColor: TAG_BORDER,
};

const PHARMACY_TAG_TEXT_STYLE = {
  color: BROWN,
  fontSize: 10,
  fontWeight: "700" as const,
};

const MEDICATION_HELP_TOPICS: MedicationHelpTopic[] = [
  {
    id: "storage",
    illustration: medicationHelpIllustrations.storage,
    titleKey: "access.pharmacy.medication_help_storage_title",
    summaryKey: "access.pharmacy.medication_help_storage_summary",
    bodyKey: "access.pharmacy.medication_help_storage_body",
    nextStepKey: "access.pharmacy.medication_help_storage_next",
    sourceUrl:
      "https://www.fda.gov/drugs/information-consumers-and-patients-drugs/stop-learn-go-tips-talking-your-pharmacist-learn-how-use-medicines-safely",
  },
  {
    id: "injection_disposal",
    illustration: medicationHelpIllustrations.injection_disposal,
    titleKey: "access.pharmacy.medication_help_injection_title",
    summaryKey: "access.pharmacy.medication_help_injection_summary",
    bodyKey: "access.pharmacy.medication_help_injection_body",
    nextStepKey: "access.pharmacy.medication_help_injection_next",
    sourceUrl:
      "https://www.fda.gov/medical-devices/consumer-products/safely-using-sharps-needles-and-syringes-home-work-and-travel",
  },
  {
    id: "missed_damaged",
    illustration: medicationHelpIllustrations.missed_damaged,
    titleKey: "access.pharmacy.medication_help_missed_title",
    summaryKey: "access.pharmacy.medication_help_missed_summary",
    bodyKey: "access.pharmacy.medication_help_missed_body",
    nextStepKey: "access.pharmacy.medication_help_missed_next",
    sourceUrl:
      "https://www.fda.gov/drugs/information-consumers-and-patients-drugs/stop-learn-go-tips-talking-your-pharmacist-learn-how-use-medicines-safely",
  },
  {
    id: "delivery_refills",
    illustration: medicationHelpIllustrations.delivery_refills,
    titleKey: "access.pharmacy.medication_help_delivery_title",
    summaryKey: "access.pharmacy.medication_help_delivery_summary",
    bodyKey: "access.pharmacy.medication_help_delivery_body",
    nextStepKey: "access.pharmacy.medication_help_delivery_next",
    sourceUrl:
      "https://www.healthcare.gov/using-marketplace-coverage/prescription-medications/",
  },
  {
    id: "travel",
    illustration: medicationHelpIllustrations.travel,
    titleKey: "access.pharmacy.medication_help_travel_title",
    summaryKey: "access.pharmacy.medication_help_travel_summary",
    bodyKey: "access.pharmacy.medication_help_travel_body",
    nextStepKey: "access.pharmacy.medication_help_travel_next",
    sourceUrl:
      "https://www.cdc.gov/yellow-book/hcp/travelers-with-additional-considerations/traveling-with-prohibited-or-restricted-medications.html",
  },
];

const DEFAULT_MEDICATION_STORAGE_PROFILE: MedicationStorageProfile = {
  statusKey: "access.pharmacy.medication_help_storage_check_label",
  statusTone: "neutral",
  windowKey: "access.pharmacy.medication_help_storage_unknown_window",
  detailKey: "access.pharmacy.medication_help_storage_unknown_detail",
};

function getMedicationStorageProfile(
  medication: DisplayMedication,
): MedicationStorageProfile {
  const name = medication.name.toLowerCase();

  if (name.includes("gonal-f")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_refrigerate",
      statusTone: "blue",
      windowKey: "access.pharmacy.medication_help_storage_gonal_window",
      detailKey: "access.pharmacy.medication_help_storage_gonal_detail",
    };
  }

  if (name.includes("cetrotide")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_refrigerate",
      statusTone: "blue",
      windowKey: "access.pharmacy.medication_help_storage_cetrotide_window",
      detailKey: "access.pharmacy.medication_help_storage_cetrotide_detail",
    };
  }

  if (name.includes("ovidrel")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_refrigerate",
      statusTone: "blue",
      windowKey: "access.pharmacy.medication_help_storage_ovidrel_window",
      detailKey: "access.pharmacy.medication_help_storage_ovidrel_detail",
    };
  }

  if (name.includes("endometrin")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_room_temperature",
      statusTone: "green",
      windowKey: "access.pharmacy.medication_help_storage_endometrin_window",
      detailKey: "access.pharmacy.medication_help_storage_endometrin_detail",
    };
  }

  if (name.includes("dexamethasone")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_room_temperature",
      statusTone: "green",
      windowKey: "access.pharmacy.medication_help_storage_dexamethasone_window",
      detailKey: "access.pharmacy.medication_help_storage_dexamethasone_detail",
    };
  }

  return DEFAULT_MEDICATION_STORAGE_PROFILE;
}

function getMedicationForm(value?: string | null): MedicationForm {
  const normalized = value?.toLowerCase() ?? "";
  if (normalized.includes("tablet") || normalized.includes("pill")) {
    return "tablet";
  }
  if (normalized.includes("syringe")) return "syringe";
  if (normalized.includes("vial") || normalized.includes("solution")) {
    return "vial";
  }
  if (normalized.includes("suppository")) return "suppository";
  return "pen";
}

function MedFormIcon({
  form,
  size = 32,
}: {
  form: MedicationForm;
  size?: number;
}) {
  const source =
    form === "pen"
      ? injectionPenImage
      : form === "vial" || form === "syringe"
        ? vialSyringeImage
        : pillCapsuleImage;

  return (
    <Image
      source={source}
      style={{ width: size, height: size }}
      resizeMode="contain"
    />
  );
}

function getPharmacyMedicationPrice(
  pharmacy: PharmacyDirectoryRecord,
  medicationId: string,
) {
  return (
    pharmacy.demo?.medicationPrices?.find(
      (entry) => entry.medicationId === medicationId,
    )?.price ?? null
  );
}

function PharmacySavingsProgramCard({
  program,
  onPress,
}: {
  program: PharmacySavingsProgram;
  onPress: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PharmacySavingsProgramCard");
  const coupon = program.coupon;
  const logoImage = savingsProgramLogoImages[program.id];
  const logoAssets = savingsProgramLogoAssets[program.id];

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        width: 204,
        minHeight: 218,
        padding: 14,
        borderRadius: 20,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {coupon ? (
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            opacity: 0.26,
          }}
        >
          <SvgUri
            uri={trumpRxCouponTextureAsset.uri}
            width="100%"
            height="100%"
          />
        </View>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`${program.name}. ${t("access.pharmacy.savings_programs_view_details")}`}
        onPress={() => {
          capturePress(pressLabel(program.name), "savings_program_card");
          onPress();
        }}
        style={{ flex: 1, zIndex: 1 }}
      >
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <View
            style={{
              width: "100%",
              alignItems: "center",
              gap: 5,
            }}
          >
            {coupon ? (
              <Image
                source={trumpRxCouponEagleImage}
                resizeMode="contain"
                style={{ width: 68, height: 60, opacity: 0.9 }}
              />
            ) : logoImage ? (
              <Image
                source={logoImage}
                resizeMode="contain"
                style={{ width: 90, height: 30 }}
              />
            ) : logoAssets ? (
              <View
                style={{
                  height: 30,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                }}
              >
                {logoAssets.map((asset, index) => (
                  <SvgUri
                    key={`${program.id}-logo-${index}`}
                    uri={asset.uri}
                    width={index === 0 ? "68" : "62"}
                    height="22"
                  />
                ))}
              </View>
            ) : (
              <HeartHandshake
                size={29}
                color={EMBER.colors.warm}
                strokeWidth={1.5}
                style={{ marginBottom: 3 }}
              />
            )}
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{
                color: EMBER.colors.text,
                fontSize: 15,
                fontWeight: "800",
                lineHeight: 18,
                textAlign: "center",
              }}
            >
              {program.name}
            </Text>
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{
                color: EMBER.colors.textLight,
                fontSize: 10.5,
                textAlign: "center",
              }}
            >
              {program.sponsor}
            </Text>
          </View>
          <View
            style={{
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              paddingTop: 12,
            }}
          >
            <Text
              className="font-dm-sans"
              numberOfLines={3}
              style={{
                flexShrink: 1,
                color: EMBER.colors.textLight,
                fontSize: 11,
                lineHeight: 16,
                textAlign: "center",
              }}
            >
              {program.headline}
            </Text>
            <ChevronRight
              size={17}
              color={EMBER.colors.textFaint}
              strokeWidth={1.6}
            />
          </View>
        </View>
      </Pressable>
    </View>
  );
}

function SavingsProgramDialog({
  program,
  onClose,
}: {
  program: PharmacySavingsProgram | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  if (!program) return null;

  return (
    <ProgramDetailsDialog
      title={program.name}
      subtitle={program.sponsor}
      icon={HeartHandshake}
      savingsLabel={program.savingsLabel}
      summary={program.headline}
      facts={[
        {
          label: t("access.pharmacy.savings_programs_eligibility"),
          icon: UserCheck,
          value: program.eligibilityLabel,
        },
        {
          label: t("access.pharmacy.savings_programs_process"),
          icon: ListChecks,
          value: program.processLabel,
        },
        {
          label: t("access.pharmacy.savings_programs_medications_title"),
          icon: Pill,
          value: program.medicationLabel,
        },
      ]}
      source={{ url: program.sourceUrl, checkedOn: program.lastVerifiedOn }}
      onClose={onClose}
      analytics={{
        close: "access.pharmacy.savings_programs_close",
        backdrop: "dismiss_savings_program",
        openSource: "access.pharmacy.savings_programs_open",
      }}
      testID="savings-program-dialog"
    />
  );
}

function MedicationHelpCard({
  topic,
  onPress,
}: {
  topic: MedicationHelpTopic;
  onPress: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationHelpCard");

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t(topic.titleKey)}
      onPress={() => {
        capturePress(pressLabel(t(topic.titleKey)), topic.titleKey);
        onPress();
      }}
      style={{
        ...(GLASS_CARD_STYLE as object),
        width: 220,
        minHeight: 146,
        padding: 14,
        borderRadius: 18,
        gap: 12,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Image
          source={topic.illustration}
          resizeMode="contain"
          style={{ width: 72, height: 56, marginLeft: -5 }}
        />
        <ChevronRight
          size={16}
          color={EMBER.colors.textFaint}
          strokeWidth={1.6}
        />
      </View>
      <View style={{ gap: 5 }}>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.text,
            fontSize: 14,
            fontWeight: "800",
            lineHeight: 18,
          }}
        >
          {t(topic.titleKey)}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 11,
            lineHeight: 16,
          }}
        >
          {t(topic.summaryKey)}
        </Text>
      </View>
    </Pressable>
  );
}

function MedicationHelpMedicationList({
  topicId,
  medications,
}: {
  topicId: MedicationHelpTopicId;
  medications: DisplayMedication[];
}) {
  const t = useTranslations();
  const sectionKey =
    topicId === "storage"
      ? "access.pharmacy.medication_help_medications_section"
      : topicId === "injection_disposal"
        ? "access.pharmacy.medication_help_injectables_section"
        : topicId === "missed_damaged"
          ? "access.pharmacy.medication_help_missed_section"
          : topicId === "delivery_refills"
            ? "access.pharmacy.medication_help_delivery_section"
            : "access.pharmacy.medication_help_travel_section";

  if (medications.length === 0) {
    return (
      <View style={{ gap: 6 }}>
        <SectionTitle>{t(sectionKey)}</SectionTitle>
        <SupportingSubtitleText style={{ marginTop: 0 }}>
          {t("access.pharmacy.medication_help_no_medications")}
        </SupportingSubtitleText>
      </View>
    );
  }

  return (
    <View style={{ gap: 9 }}>
      <SectionTitle>{t(sectionKey)}</SectionTitle>
      {medications.map((medication) => {
        const storage = getMedicationStorageProfile(medication);
        const showStorage = topicId === "storage" || topicId === "travel";
        const rowDetailKey =
          topicId === "injection_disposal"
            ? "access.pharmacy.medication_help_injection_row"
            : topicId === "missed_damaged"
              ? "access.pharmacy.medication_help_missed_row"
              : topicId === "delivery_refills"
                ? "access.pharmacy.medication_help_delivery_row"
                : null;

        return (
          <View
            key={medication.id}
            style={{
              gap: 5,
              padding: 10,
              borderRadius: 13,
              backgroundColor: semanticColors.surface.subtle,
              borderWidth: 1,
              borderColor: semanticColors.border.strong,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 9,
              }}
            >
              <MedFormIcon form={medication.form} />
              <View style={{ flex: 1, gap: 5 }}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <View style={{ flex: 1, gap: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.text,
                        fontSize: 13,
                        fontWeight: "700",
                      }}
                    >
                      {medication.name}
                      {medication.dosage ? ` · ${medication.dosage}` : ""}
                    </Text>
                    {medication.route ? (
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.textFaint,
                          fontSize: 11,
                          lineHeight: 15,
                        }}
                      >
                        {medication.route}
                      </Text>
                    ) : null}
                  </View>
                  {showStorage ? (
                    <Tag
                      label={t(storage.statusKey)}
                      tone={storage.statusTone}
                      variant="soft"
                    />
                  ) : null}
                </View>

                {showStorage ? (
                  <View style={{ gap: 2 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.text,
                        fontSize: 12,
                        lineHeight: 17,
                      }}
                    >
                      {t(storage.windowKey)}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textLight,
                        fontSize: 11,
                        lineHeight: 16,
                      }}
                    >
                      {t(storage.detailKey)}
                    </Text>
                  </View>
                ) : rowDetailKey ? (
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.textLight,
                      fontSize: 11,
                      lineHeight: 16,
                    }}
                  >
                    {t(rowDetailKey)}
                  </Text>
                ) : null}
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function MedicationHelpDialog({
  topic,
  onClose,
  medications,
}: {
  topic: MedicationHelpTopic | null;
  onClose: () => void;
  medications: DisplayMedication[];
}) {
  const t = useTranslations();
  if (!topic) return null;

  return (
    <Dialog
      size="wide"
      visible
      onClose={onClose}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: "100%" }}
      contentStyle={{
        maxHeight: "100%",
        paddingTop: 28,
        paddingHorizontal: 20,
        paddingBottom: 20,
      }}
      testID="medication-help-dialog"
    >
      <ScrollView
        style={{ maxHeight: "100%" }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 16 }}
      >
        <Image
          source={topic.illustration}
          resizeMode="contain"
          style={{ width: 88, height: 72, alignSelf: "center" }}
        />

        <DialogHeader
          align="center"
          style={{ width: "100%" }}
          title={t(topic.titleKey)}
          subtitle={t(topic.summaryKey)}
          subtitleStyle={{ width: "100%", textAlign: "center" }}
        />

        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 13,
            lineHeight: 20,
          }}
        >
          {t(topic.bodyKey)}
        </Text>

        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 13,
            lineHeight: 20,
          }}
        >
          {t(topic.nextStepKey)}
        </Text>

        <MedicationHelpMedicationList
          topicId={topic.id}
          medications={medications}
        />

        <DialogActions style={{ marginTop: 2 }}>
          <PrimaryButton
            label={t("access.pharmacy.medication_help_open_source")}
            onPress={() => void Linking.openURL(topic.sourceUrl)}
            flex={1}
            trailing={
              <ExternalLink
                size={14}
                color={semanticColors.action.primary.foreground}
                strokeWidth={2}
              />
            }
          />
        </DialogActions>
      </ScrollView>
    </Dialog>
  );
}

function PharmacyInfoCard({
  icon: Icon,
  title,
  body,
  framed = true,
  footer,
  illustration,
}: {
  icon?: LucideIcon;
  title?: string;
  body: string;
  framed?: boolean;
  footer?: ReactNode;
  illustration?: number;
}) {
  return (
    <View
      style={
        framed
          ? { ...(GLASS_CARD_STYLE as object), padding: 18, gap: 8 }
          : { padding: 0, gap: 8 }
      }
    >
      {illustration && !title ? (
        <View style={{ alignItems: "center", gap: 8 }}>
          <Image
            source={illustration}
            resizeMode="contain"
            style={{ width: 86, height: 60 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 13,
              lineHeight: 19,
              maxWidth: 360,
              textAlign: "center",
            }}
          >
            {body}
          </Text>
        </View>
      ) : (
        <>
          <View
            style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}
          >
            {illustration ? (
              <Image
                source={illustration}
                resizeMode="contain"
                style={{ width: 64, height: 52, marginTop: -5 }}
              />
            ) : Icon ? (
              <Icon
                size={18}
                color={EMBER.colors.warm}
                strokeWidth={1.6}
                style={{ marginTop: 2 }}
              />
            ) : null}
            {title ? (
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  color: EMBER.colors.text,
                  fontSize: 15,
                  fontWeight: "700",
                }}
              >
                {title}
              </Text>
            ) : null}
          </View>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 13,
              lineHeight: 19,
            }}
          >
            {body}
          </Text>
        </>
      )}
      {footer ? <View style={{ marginTop: 4 }}>{footer}</View> : null}
    </View>
  );
}

function PharmacyPatientQuoteRow({
  quote,
  divider = false,
  onPress,
}: {
  quote: PharmacyPatientQuote;
  divider?: boolean;
  onPress: () => void;
}) {
  const t = useTranslations();
  const subtitle = [
    t("access.pharmacy.quote_added_date", {
      params: { date: quote.quoteDate },
    }),
    quote.documentName,
  ]
    .filter(Boolean)
    .join(" · ");

  // Resolve the pharmacy's logo when the free-text `pharmacyName` on the
  // patient's uploaded quote pairs to one of our concierge target pharmacies.
  const matchedDispatchId = matchDispatchIdFromPharmacyName(quote.pharmacyName);
  const logo = matchedDispatchId ? pharmacyLogoImages[matchedDispatchId] : null;

  return (
    <Row
      surface="plain"
      title={quote.pharmacyName}
      subtitle={subtitle}
      subtitleVariant="meta"
      leading={
        logo ? (
          <Image
            source={logo}
            resizeMode="contain"
            style={{ width: 42, height: 28 }}
          />
        ) : (
          <Store size={18} color={EMBER.colors.textLight} strokeWidth={1.7} />
        )
      }
      trailing={
        <Text
          className="font-dm-sans"
          style={{
            color:
              quote.totalPrice != null
                ? EMBER.colors.text
                : EMBER.colors.textLight,
            fontSize: quote.totalPrice != null ? 16 : 11,
            fontWeight: "800",
          }}
        >
          {quote.totalPrice != null
            ? `$${quote.totalPrice.toLocaleString("en-US")}`
            : t("access.pharmacy.quote_price_not_added")}
        </Text>
      }
      chevron
      onPress={onPress}
      action="pharmacy_patient_quote_row"
      accessibilityLabel={t("access.pharmacy.quote_details_a11y", {
        params: { name: quote.pharmacyName },
      })}
      style={{
        paddingHorizontal: 12,
        paddingVertical: 16,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: semanticColors.border.subtle,
      }}
    />
  );
}

/**
 * Compact "Waiting for reply" row for a dispatched pharmacy that hasn't
 * emailed a quote back yet (or the patient hasn't uploaded one). Trailing
 * `Upload` button opens PharmacyQuoteCaptureModal pre-tagged with this
 * pharmacy's name.
 */
function PharmacySentPharmacyRow({
  submission,
  divider,
  onUploadPress,
}: {
  submission: PharmacyQuoteSubmission;
  divider: boolean;
  onUploadPress: () => void;
}) {
  const t = useTranslations();
  const logo = submission.pharmacySlug
    ? pharmacyLogoImages[submission.pharmacySlug]
    : undefined;
  // `queued` = ops have not filled this pharmacy's form yet, `submitted` =
  // sent, `failed` = could not be sent (reason written by ops).
  const subtitle =
    submission.status === "failed"
      ? [
          t("access.pharmacy.quote_comparison_request_not_sent"),
          submission.failureReason,
        ]
          .filter(Boolean)
          .join(" — ")
      : submission.status === "queued"
        ? t("access.pharmacy.quote_comparison_request_being_sent")
        : t("access.pharmacy.quote_comparison_waiting_reply");
  return (
    <Row
      surface="plain"
      leading={
        logo ? (
          <Image
            source={logo}
            resizeMode="contain"
            style={{ width: 42, height: 28 }}
          />
        ) : (
          <Store size={18} color={EMBER.colors.textLight} strokeWidth={1.7} />
        )
      }
      title={submission.pharmacyName}
      titleNumberOfLines={1}
      subtitle={subtitle}
      subtitleVariant="meta"
      subtitleNumberOfLines={2}
      trailing={
        <PharmacyActionButton
          icon={UploadCloud}
          label={t("access.pharmacy.quote_comparison_upload_short")}
          action="access.pharmacy.quote_comparison_upload_short"
          onPress={onUploadPress}
          secondary
        />
      }
      chevron={false}
      style={{
        paddingHorizontal: 12,
        paddingVertical: 18,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: "rgba(107, 85, 72, 0.1)",
      }}
    />
  );
}

/**
 * Ready-to-fill card. Visible once the patient has uploaded at least one
 * pharmacy quote. The patient picks *which* pharmacy they actually want to
 * fill at — not always the cheapest, sometimes a slightly higher quote from a
 * pharmacy they prefer for delivery, service, etc. Selection defaults to the
 * cheapest priced upload as a nudge but the patient can override with a tap.
 * The message template regenerates around the picked pharmacy.
 */
function ReadyToFillCard({
  quoteOptions,
  patientName,
  clinicName,
}: {
  quoteOptions: {
    id: string;
    pharmacyName: string;
    priceCents: number | null;
  }[];
  patientName: string;
  clinicName: string;
}) {
  const t = useTranslations();
  const defaultQuoteId =
    quoteOptions
      .filter((q) => q.priceCents !== null)
      .sort(
        (a, b) => (a.priceCents ?? Infinity) - (b.priceCents ?? Infinity),
      )[0]?.id ??
    quoteOptions[0]?.id ??
    "";
  const [selectedId, setSelectedId] = useState<string>(defaultQuoteId);
  const [copied, setCopied] = useState(false);

  const selected =
    quoteOptions.find((q) => q.id === selectedId) ?? quoteOptions[0];
  const pharmacyName = selected?.pharmacyName ?? "";

  const message = t(
    "access.pharmacy.quote_comparison_clinic_message_template",
    {
      params: {
        pharmacyName,
        patientName:
          patientName ||
          t("access.pharmacy.quote_comparison_your_name_placeholder"),
        clinicName:
          clinicName ||
          t("access.pharmacy.quote_comparison_your_clinic_placeholder"),
      },
    },
  );
  const copyLabel = copied
    ? t("access.pharmacy.quote_comparison_copy_done")
    : t("access.pharmacy.quote_comparison_copy_clinic_message");

  const handleCopy = async () => {
    try {
      if (Platform.OS === "web" && typeof navigator !== "undefined") {
        await navigator.clipboard?.writeText(message);
      } else {
        await Clipboard.setStringAsync(message);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch (error) {
      console.error("[pharmacy-concierge] copy failed", error);
    }
  };

  return (
    <View
      style={{
        borderRadius: 16,
        borderWidth: 1,
        borderColor: "rgba(107, 85, 72, 0.1)",
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        padding: 14,
        gap: 12,
      }}
    >
      <PharmacyInfoCard
        icon={HeartHandshake}
        title={t("access.pharmacy.quote_comparison_ready_to_fill_title")}
        body={t("access.pharmacy.quote_comparison_ready_to_fill_body")}
        framed={false}
      />
      {quoteOptions.length > 1 ? (
        <View style={{ gap: 6 }}>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 11,
              fontWeight: "800",
              letterSpacing: 0.8,
              textTransform: "uppercase",
            }}
          >
            {t("access.pharmacy.quote_comparison_ready_to_fill_choose_label")}
          </Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {quoteOptions.map((option) => {
              const isSelected = option.id === selectedId;
              return (
                <Pressable
                  key={option.id}
                  onPress={() => setSelectedId(option.id)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: isSelected }}
                  accessibilityLabel={option.pharmacyName}
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: isSelected
                      ? EMBER.colors.warm
                      : EMBER.colors.border,
                    backgroundColor: isSelected
                      ? "rgba(139, 58, 26, 0.08)"
                      : "transparent",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: isSelected
                        ? EMBER.colors.warm
                        : EMBER.colors.textMid,
                      fontSize: 12,
                      fontWeight: isSelected ? "600" : "400",
                    }}
                  >
                    {option.pharmacyName}
                  </Text>
                  {option.priceCents !== null ? (
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: isSelected
                          ? EMBER.colors.warm
                          : EMBER.colors.textLight,
                        fontSize: 11,
                        fontWeight: "600",
                      }}
                    >
                      {`$${Math.round(option.priceCents / 100).toLocaleString(
                        "en-US",
                      )}`}
                    </Text>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}
      <View
        style={{
          padding: 12,
          borderRadius: 12,
          backgroundColor: "rgba(255,255,255,0.5)",
          borderWidth: 1,
          borderColor: "rgba(107, 85, 72, 0.1)",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.text,
            fontSize: 12,
            lineHeight: 17,
          }}
        >
          {message}
        </Text>
      </View>
      <PrimaryButton label={copyLabel} onPress={handleCopy} fullWidth />
    </View>
  );
}

function PharmacyDemoQuoteRow({
  pharmacy,
  quote,
  divider = false,
  onPress,
}: {
  pharmacy: PharmacyDirectoryRecord;
  quote: PharmacyDemoQuote;
  divider?: boolean;
  onPress: () => void;
}) {
  const t = useTranslations();
  const logo = pharmacyLogoImages[pharmacy.id];

  return (
    <Row
      surface="plain"
      title={pharmacy.name}
      subtitle={t("access.pharmacy.demo_quote_label")}
      subtitleVariant="meta"
      leading={
        logo ? (
          <Image
            source={logo}
            resizeMode="contain"
            style={{ width: 42, height: 28 }}
          />
        ) : (
          <Store size={18} color={EMBER.colors.textLight} strokeWidth={1.7} />
        )
      }
      trailing={
        <View style={{ alignItems: "flex-end", gap: 3 }}>
          {quote.price != null ? (
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.text,
                fontSize: 16,
                fontWeight: "800",
              }}
            >
              {`$${quote.price.toLocaleString("en-US")}`}
            </Text>
          ) : (
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.textLight,
                fontSize: 11,
                fontWeight: "700",
              }}
            >
              {t("access.pharmacy.contact_for_pricing")}
            </Text>
          )}
          {quote.estimatedSavings ? (
            <Tag
              label={t("access.pharmacy.demo_quote_savings", {
                params: {
                  amount: quote.estimatedSavings.toLocaleString("en-US"),
                },
              })}
              tone="green"
              variant="soft"
              style={PHARMACY_TAG_STYLE}
              textStyle={PHARMACY_TAG_TEXT_STYLE}
            />
          ) : null}
        </View>
      }
      chevron
      onPress={onPress}
      action="pharmacy_demo_quote_row"
      accessibilityLabel={t("access.pharmacy.quote_details_a11y", {
        params: { name: pharmacy.name },
      })}
      style={{
        paddingHorizontal: 12,
        paddingVertical: 12,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: semanticColors.border.subtle,
      }}
    />
  );
}

/**
 * Whole Quotes-tab layout. One glass PharmacyInfoCard on top with adaptive
 * copy, then a rows container that either:
 *   - shows the sent-summary + one row per pharmacy submission of the
 *     submitted/done request (with an "Upload" trailing button when the
 *     patient hasn't uploaded the pharmacy's quote yet, or the canonical
 *     PharmacyPatientQuoteRow once they have), OR
 *   - shows the pre-existing patientQuotes / demo rows when there's no
 *     concierge request in flight.
 * "Upload from another pharmacy" secondary CTA sits below the rows so the
 * patient can still upload quotes from outside Louise's send list.
 */
function PharmacyQuotesTabView({
  quoteRequest,
  patientQuotes,
  onOpenPatientQuote,
  onOpenUploadFor,
}: {
  quoteRequest: PharmacyQuoteRequest | null;
  patientQuotes: PharmacyPatientQuote[];
  onOpenPatientQuote: (quote: PharmacyPatientQuote) => void;
  onOpenUploadFor: (pharmacyName: string) => void;
}) {
  const t = useTranslations();
  const sentRequest =
    quoteRequest &&
    (quoteRequest.status === "submitted" || quoteRequest.status === "done")
      ? quoteRequest
      : null;
  const submissions = sentRequest?.submissions ?? [];
  // Name + clinic the patient gave at submit time, for the copy-for-clinic
  // message below.
  const submittedPatientName = [
    sentRequest?.patientSnapshot?.firstName,
    sentRequest?.patientSnapshot?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const submittedClinicName = sentRequest?.clinicSnapshot?.clinicName ?? "";

  // Pair each pharmacy submission to the first uploaded quote whose free-text
  // pharmacyName resolves to that pharmacy's slug.
  const matchedByDispatch = new Map<string, PharmacyPatientQuote>();
  const unmatchedQuotes: PharmacyPatientQuote[] = [];
  for (const quote of patientQuotes) {
    const dispatchId = matchDispatchIdFromPharmacyName(quote.pharmacyName);
    if (dispatchId && !matchedByDispatch.has(dispatchId)) {
      matchedByDispatch.set(dispatchId, quote);
    } else {
      unmatchedQuotes.push(quote);
    }
  }

  // Every uploaded quote — matched or not — is a valid pick for the
  // prescriber-handoff step. The patient may deliberately choose a slightly
  // more expensive pharmacy for delivery timing, preferred service, etc., so
  // we surface all of them and let them decide.
  const readyToFillOptions = patientQuotes.map((q) => ({
    id: q.id,
    pharmacyName: q.pharmacyName,
    priceCents:
      typeof q.totalPrice === "number" && q.totalPrice > 0
        ? q.totalPrice * 100
        : null,
  }));

  const sentDate = sentRequest?.submittedAt
    ? new Date(sentRequest.submittedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "";

  const body = !sentRequest
    ? t("access.pharmacy.quote_comparison_body_idle")
    : sentRequest.status === "done"
      ? t("access.pharmacy.quote_comparison_body_done", {
          params: { total: String(sentRequest.totalCount), date: sentDate },
        })
      : t("access.pharmacy.quote_comparison_body_waiting", {
          params: {
            sent: String(sentRequest.submittedCount),
            total: String(sentRequest.totalCount),
            date: sentDate,
          },
        });

  const showRowsContainer = submissions.length > 0 || patientQuotes.length > 0;

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        padding: 14,
        gap: 12,
        overflow: "hidden",
      }}
    >
      <PharmacyInfoCard
        title={t("access.pharmacy.quote_comparison_title")}
        icon={Mail}
        body={body}
        framed={false}
      />

      {showRowsContainer ? (
        <View
          style={{
            borderRadius: 16,
            borderWidth: 1,
            borderColor: semanticColors.border.subtle,
            backgroundColor: withAlpha(semanticColors.surface.default, 0.2),
            overflow: "hidden",
          }}
        >
          {submissions.length > 0 ? (
            <>
              {submissions.map((submission, index) => {
                const matched = submission.pharmacySlug
                  ? matchedByDispatch.get(submission.pharmacySlug)
                  : undefined;
                if (matched) {
                  return (
                    <PharmacyPatientQuoteRow
                      key={submission.id}
                      quote={matched}
                      divider={index > 0}
                      onPress={() => onOpenPatientQuote(matched)}
                    />
                  );
                }
                return (
                  <PharmacySentPharmacyRow
                    key={submission.id}
                    submission={submission}
                    divider={index > 0}
                    onUploadPress={() =>
                      onOpenUploadFor(submission.pharmacyName)
                    }
                  />
                );
              })}
              {unmatchedQuotes.map((quote, index) => (
                <PharmacyPatientQuoteRow
                  key={quote.id}
                  quote={quote}
                  divider={submissions.length > 0 || index > 0}
                  onPress={() => onOpenPatientQuote(quote)}
                />
              ))}
            </>
          ) : (
            // Idle state: only the patient's real uploaded quotes. Demo /
            // illustrative rows belong to the "All Pharmacies" tab where price
            // shopping happens — surfacing them here made a demo-quote detail
            // dialog say "Sample only for walkthroughs" for a Quotes-tab row,
            // which is confusing once the concierge flow is the primary path.
            <>
              {patientQuotes.map((quote, index) => (
                <PharmacyPatientQuoteRow
                  key={quote.id}
                  quote={quote}
                  divider={index > 0}
                  onPress={() => onOpenPatientQuote(quote)}
                />
              ))}
            </>
          )}
        </View>
      ) : null}

      <ScanTileButton
        icon={UploadCloud}
        title={t("access.pharmacy.quote_comparison_upload_from_other")}
        subtitle={t("access.pharmacy.quote_comparison_upload_from_other_hint")}
        action="access.pharmacy.quote_comparison_upload_from_other"
        onPress={() => onOpenUploadFor("")}
      />

      {readyToFillOptions.length > 0 ? (
        <ReadyToFillCard
          quoteOptions={readyToFillOptions}
          patientName={submittedPatientName}
          clinicName={submittedClinicName}
        />
      ) : null}
    </View>
  );
}

function PharmacyQuoteDetailDialog({
  selection,
  onClose,
}: {
  selection: SelectedPharmacyQuote | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const getDownloadUrl = useGetDownloadUrl();
  if (!selection) return null;

  const isPatientQuote = selection.kind === "patient";
  const patientUploadId = isPatientQuote ? selection.quote.uploadId : null;

  const openAttachment = async () => {
    if (!patientUploadId) return;
    try {
      const url = await getDownloadUrl.mutateAsync({
        uploadId: patientUploadId,
        disposition: "inline",
      });
      if (typeof url === "string") await Linking.openURL(url);
    } catch (error) {
      console.error("Failed to open pharmacy quote attachment:", error);
    }
  };
  const title = isPatientQuote
    ? selection.quote.pharmacyName
    : selection.pharmacy.name;
  const subtitle = isPatientQuote
    ? t("access.pharmacy.quote_detail_patient_subtitle", {
        params: { date: selection.quote.quoteDate },
      })
    : t("access.pharmacy.quote_detail_demo_subtitle");
  const total = isPatientQuote
    ? selection.quote.totalPrice
    : selection.quote.price;

  return (
    <Dialog
      size="wide"
      visible
      onClose={onClose}
      closeButton="minimal"
      contentStyle={{ paddingTop: 28, paddingHorizontal: 20 }}
      testID="pharmacy-quote-detail-dialog"
    >
      <DialogHeader title={title} subtitle={subtitle} />
      <View
        style={{
          marginTop: 18,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: semanticColors.border.subtle,
          backgroundColor: withAlpha(semanticColors.surface.default, 0.2),
          overflow: "hidden",
        }}
      >
        <Row
          surface="plain"
          title={t("access.pharmacy.quote_detail_total")}
          trailing={
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.text,
                fontSize: 16,
                fontWeight: "800",
              }}
            >
              {total != null
                ? `$${total.toLocaleString("en-US")}`
                : t("access.pharmacy.quote_price_not_added")}
            </Text>
          }
          chevron={false}
          paddingHorizontal={12}
          paddingVertical={11}
        />
        {!isPatientQuote && selection.quote.estimatedSavings ? (
          <Row
            surface="plain"
            title={t("access.pharmacy.quote_detail_estimated_savings")}
            trailing={
              <Tag
                label={t("access.pharmacy.demo_quote_savings", {
                  params: {
                    amount:
                      selection.quote.estimatedSavings.toLocaleString("en-US"),
                  },
                })}
                tone="green"
                variant="soft"
                style={PHARMACY_TAG_STYLE}
                textStyle={PHARMACY_TAG_TEXT_STYLE}
              />
            }
            chevron={false}
            paddingHorizontal={12}
            paddingVertical={11}
            style={{
              borderTopWidth: 1,
              borderTopColor: semanticColors.border.subtle,
            }}
          />
        ) : null}
        <Row
          surface="plain"
          title={t("access.pharmacy.quote_detail_source")}
          trailing={
            <Text
              className="font-dm-sans"
              style={{ color: EMBER.colors.textLight, fontSize: 12 }}
            >
              {isPatientQuote
                ? selection.quote.source === "uploaded"
                  ? t("access.pharmacy.quote_detail_uploaded")
                  : t("access.pharmacy.quote_detail_manual")
                : t("access.pharmacy.quote_detail_demo")}
            </Text>
          }
          chevron={false}
          paddingHorizontal={12}
          paddingVertical={11}
          style={{
            borderTopWidth: 1,
            borderTopColor: semanticColors.border.subtle,
          }}
        />
        {isPatientQuote && selection.quote.documentName ? (
          <Row
            surface="plain"
            title={t("access.pharmacy.quote_detail_document")}
            trailing={
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  maxWidth: 170,
                  color: patientUploadId
                    ? EMBER.colors.text
                    : EMBER.colors.textLight,
                  fontSize: 12,
                  fontWeight: patientUploadId ? "700" : "400",
                }}
              >
                {selection.quote.documentName}
              </Text>
            }
            chevron={!!patientUploadId}
            onPress={patientUploadId ? openAttachment : undefined}
            paddingHorizontal={12}
            paddingVertical={11}
            style={{
              borderTopWidth: 1,
              borderTopColor: semanticColors.border.subtle,
            }}
          />
        ) : null}
      </View>
      {isPatientQuote && selection.quote.note ? (
        <View style={{ gap: 5, marginTop: 16 }}>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.text,
              fontSize: 12,
              fontWeight: "800",
            }}
          >
            {t("access.pharmacy.quote_detail_note")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 13,
              lineHeight: 19,
            }}
          >
            {selection.quote.note}
          </Text>
        </View>
      ) : null}
      {!isPatientQuote ? (
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 16,
            color: EMBER.colors.textLight,
            fontSize: 13,
            lineHeight: 19,
          }}
        >
          {t("access.pharmacy.demo_detail_quote_body")}
        </Text>
      ) : null}
      <DialogActions style={{ marginTop: 18 }}>
        <SecondaryButton
          label={t("access.pharmacy.quote_detail_close")}
          onPress={onClose}
          flex={1}
        />
      </DialogActions>
    </Dialog>
  );
}

function PharmacyQuoteCaptureModal({
  visible,
  onClose,
  initialPharmacyName,
}: {
  visible: boolean;
  onClose: () => void;
  /**
   * Pre-fills the pharmacy field when the modal opens. Used by the concierge
   * flow to attribute uploaded quotes to specific dispatched pharmacies
   * (Rosemont, APRI, etc.) without asking the patient to retype the name.
   *
   * TODO(pharmacy-quote-ocr): once the doc processing pipeline exposes a
   * `pharmacy_quote` handler, fire `useDocumentSourceTransfer` between file
   * pick and the `totalPrice` input so the price OCR-prefills from the
   * uploaded PDF. Today the patient enters it manually.
   */
  initialPharmacyName?: string;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PharmacyQuoteCaptureModal");
  const [pharmacyName, setPharmacyName] = useState(initialPharmacyName ?? "");
  const [totalPrice, setTotalPrice] = useState("");
  const [quoteDate, setQuoteDate] = useState(getTodayString());
  const [note, setNote] = useState("");
  const [quoteFile, setQuoteFile] = useState<PharmacyQuoteFile | null>(null);
  const [busySource, setBusySource] = useState<
    "camera" | "document" | "save" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const createDocument = useCreateDocument();
  const getUploadUrl = useGetUploadUrl();
  const uploadFile = useUploadFile();
  const completeUpload = useCompleteUpload();

  // Re-seed the pharmacy field each time the modal opens so switching between
  // "Upload for Rosemont" and "Upload for Apthorp" doesn't leak the previous
  // pharmacy into the new draft.
  const wasVisibleRef = useRef(visible);
  useEffect(() => {
    const wasVisible = wasVisibleRef.current;
    wasVisibleRef.current = visible;
    if (visible && !wasVisible) {
      setPharmacyName(initialPharmacyName ?? "");
    }
  }, [visible, initialPharmacyName]);

  const resetDraft = () => {
    setPharmacyName(initialPharmacyName ?? "");
    setTotalPrice("");
    setQuoteDate(getTodayString());
    setNote("");
    setQuoteFile(null);
    setBusySource(null);
    setError(null);
  };

  const close = () => {
    resetDraft();
    onClose();
  };

  const selectFile = async (source: PharmacyQuoteFile["source"]) => {
    setBusySource(source);
    setError(null);
    try {
      if (source === "camera") {
        const permission = await ImagePicker.requestCameraPermissionsAsync();
        if (!permission.granted) return;
        const result = await ImagePicker.launchCameraAsync({
          mediaTypes: ["images"],
          quality: 0.82,
        });
        if (!result.canceled && result.assets?.[0]) {
          const asset = result.assets[0];
          setQuoteFile({
            source,
            name: `pharmacy-quote-${Date.now()}.jpg`,
            uri: asset.uri,
            mimeType: resolveUploadContentType({
              name: "pharmacy-quote.jpg",
              uri: asset.uri,
              mimeType: asset.mimeType,
            }),
          });
        }
      } else {
        const result = await DocumentPicker.getDocumentAsync({
          type: ["application/pdf", "image/*"],
          copyToCacheDirectory: true,
        });
        if (!result.canceled && result.assets?.[0]) {
          const asset = result.assets[0];
          setQuoteFile({
            source,
            name: asset.name,
            uri: asset.uri,
            mimeType: resolveUploadContentType({
              name: asset.name,
              uri: asset.uri,
              mimeType: asset.mimeType,
            }),
          });
        }
      }
    } catch (selectionError) {
      console.error("Failed to select pharmacy quote:", selectionError);
      setError(t("access.pharmacy.quote_upload_error"));
    } finally {
      setBusySource(null);
    }
  };

  const save = async () => {
    const parsedPrice = Number(totalPrice.replace(/[^0-9.]/g, ""));
    const hasPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;
    if (!pharmacyName.trim() || (!hasPrice && !quoteFile)) return;

    setBusySource("save");
    setError(null);
    try {
      let uploadId: string | null = quoteFile?.uploadId ?? null;
      if (quoteFile && !uploadId) {
        const upload = await getUploadUrl.mutateAsync({
          contentType: quoteFile.mimeType,
          name: quoteFile.name,
        });
        const fileData = await readFileForUpload({
          uri: quoteFile.uri,
          name: quoteFile.name,
          mimeType: quoteFile.mimeType,
        });
        await uploadFile.mutateAsync({
          uploadUrl: upload.uploadUrl,
          file: fileData,
          contentType: quoteFile.mimeType,
        });
        await completeUpload.mutateAsync(upload.uploadId);
        uploadId = upload.uploadId;
        setQuoteFile((current) =>
          current ? { ...current, uploadId: upload.uploadId } : current,
        );
      }
      await createDocument.mutateAsync({
        name: `Pharmacy quote – ${pharmacyName.trim()}`,
        documentDate: quoteDate.trim() || getTodayString(),
        tags: [
          PHARMACY_QUOTE_TAG,
          PHARMACY_QUOTE_PATIENT_TAG,
          encodePharmacyQuoteTag(
            PHARMACY_QUOTE_PHARMACY_TAG,
            pharmacyName.trim(),
          ),
          ...(hasPrice
            ? [`${PHARMACY_QUOTE_TOTAL_TAG}${Math.round(parsedPrice * 100)}`]
            : []),
          ...(note.trim()
            ? [encodePharmacyQuoteTag(PHARMACY_QUOTE_NOTE_TAG, note.trim())]
            : []),
        ],
        targetSlots: ["costs"],
        sourceType: uploadId ? "uploaded_file" : "unclassified",
        sharedWith: ["patient"],
        uploads: uploadId ? [{ id: uploadId }] : [],
      });
      close();
    } catch (saveError) {
      console.error("Failed to save pharmacy quote:", saveError);
      setError(t("access.pharmacy.quote_upload_error"));
      setBusySource(null);
    }
  };

  const parsedPrice = Number(totalPrice.replace(/[^0-9.]/g, ""));
  const canSave =
    pharmacyName.trim().length > 0 &&
    ((Number.isFinite(parsedPrice) && parsedPrice > 0) || quoteFile != null);

  const inputStyle = {
    minHeight: 42,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: semanticColors.border.subtle,
    backgroundColor: semanticColors.surface.subtle,
    paddingHorizontal: 12,
    color: EMBER.colors.text,
    fontSize: 13,
    fontFamily: "DMSans_400Regular",
  } as const;

  const renderInput = (
    label: string,
    value: string,
    onChangeText: (value: string) => void,
    placeholder: string,
    options: {
      keyboardType?: "default" | "decimal-pad";
      multiline?: boolean;
    } = {},
  ) => (
    <View style={{ gap: 6 }}>
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.textLight,
          fontSize: 11,
          fontWeight: "800",
        }}
      >
        {label}
      </Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={EMBER.colors.textFaint}
        keyboardType={options.keyboardType ?? "default"}
        multiline={options.multiline}
        style={
          options.multiline
            ? {
                ...inputStyle,
                minHeight: 72,
                paddingVertical: 10,
                textAlignVertical: "top",
              }
            : inputStyle
        }
      />
    </View>
  );

  const renderSource = (
    source: PharmacyQuoteFile["source"],
    label: string,
    Icon: LucideIcon,
  ) => (
    <Pressable
      accessibilityRole="button"
      onPress={() => {
        capturePress(pressLabel(label), `quote_source_${source}`);
        void selectFile(source);
      }}
      disabled={busySource != null}
      style={{
        flex: 1,
        minHeight: 76,
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        borderRadius: 15,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        backgroundColor: semanticColors.surface.glass,
        opacity: busySource && busySource !== source ? 0.5 : 1,
      }}
    >
      <Icon size={21} color={BROWN} strokeWidth={1.7} />
      <Text
        className="font-dm-sans"
        style={{ color: EMBER.colors.text, fontSize: 12, fontWeight: "700" }}
      >
        {busySource === source ? t("access.pharmacy.quote_opening") : label}
      </Text>
    </Pressable>
  );

  if (!visible) return null;

  return (
    <Dialog
      size="wide"
      visible={visible}
      onClose={close}
      closeButton="minimal"
      contentStyle={{
        paddingTop: 28,
        paddingHorizontal: 20,
        paddingBottom: 20,
      }}
    >
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 15, paddingBottom: 15 }}
      >
        <DialogHeader
          title={t("access.pharmacy.quote_add_title")}
          subtitle={t("access.pharmacy.quote_add_body")}
        />

        {renderInput(
          t("access.pharmacy.quote_pharmacy_label"),
          pharmacyName,
          setPharmacyName,
          t("access.pharmacy.quote_pharmacy_placeholder"),
        )}
        <View style={{ flexDirection: "row", gap: 9 }}>
          <View style={{ flex: 1 }}>
            {renderInput(
              t("access.pharmacy.quote_total_label"),
              totalPrice,
              setTotalPrice,
              t("access.pharmacy.quote_total_placeholder"),
              { keyboardType: "decimal-pad" },
            )}
          </View>
          <View style={{ flex: 1 }}>
            <DateField
              label={t("access.pharmacy.quote_date_label")}
              value={quoteDate}
              onChange={setQuoteDate}
              disabled={busySource === "save"}
            />
          </View>
        </View>
        {renderInput(
          t("access.pharmacy.quote_notes_label"),
          note,
          setNote,
          t("access.pharmacy.quote_notes_placeholder"),
          { multiline: true },
        )}

        <View style={{ gap: 8 }}>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 11,
              fontWeight: "800",
            }}
          >
            {t("access.pharmacy.quote_attach_title")}
          </Text>
          <View style={{ flexDirection: "row", gap: 9 }}>
            {renderSource(
              "camera",
              t("access.pharmacy.quote_take_photo"),
              Camera,
            )}
            {renderSource(
              "document",
              t("access.pharmacy.quote_upload_file"),
              FileText,
            )}
          </View>
          {quoteFile ? (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 7,
                paddingHorizontal: 10,
                paddingVertical: 8,
                borderRadius: 12,
                backgroundColor: withAlpha(colors.greyBrown, 0.08),
              }}
            >
              <Check size={14} color={BROWN} strokeWidth={2} />
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  flex: 1,
                  color: BROWN,
                  fontSize: 11,
                  fontWeight: "700",
                }}
              >
                {quoteFile.name}
              </Text>
            </View>
          ) : null}
        </View>

        {error ? (
          <Text
            accessibilityRole="alert"
            className="font-dm-sans"
            style={{ color: EMBER.colors.coral, fontSize: 12, lineHeight: 17 }}
          >
            {error}
          </Text>
        ) : null}
      </ScrollView>
      <DialogActions>
        <SecondaryButton
          label={t("access.pharmacy.quote_cancel")}
          onPress={close}
          flex={1}
        />
        <PrimaryButton
          label={t("access.pharmacy.quote_save")}
          onPress={() => void save()}
          disabled={!canSave}
          loading={busySource === "save"}
          flex={1}
          hapticStyle={null}
        />
      </DialogActions>
    </Dialog>
  );
}

function PharmacyLogo({ pharmacy }: { pharmacy: PharmacyDirectoryRecord }) {
  const logo = pharmacyLogoImages[pharmacy.id];

  if (logo) {
    return (
      <Image
        source={logo}
        resizeMode="contain"
        style={{ width: 116, height: 46 }}
      />
    );
  }

  return (
    <View
      style={{
        width: 42,
        height: 42,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 13,
        backgroundColor: BROWN_BG,
      }}
    >
      <Building2 size={20} color={BROWN} strokeWidth={1.7} />
    </View>
  );
}

function PharmacyCard({
  pharmacy,
  onPress,
  coverageLabel,
  cashPayLabel,
  contactForPricing,
  demoMode,
  detailsA11yLabel,
  cashPriceLabel,
  callLabel,
}: {
  pharmacy: PharmacyDirectoryRecord;
  onPress: () => void;
  coverageLabel: string;
  cashPayLabel: string;
  contactForPricing: string;
  demoMode: boolean;
  detailsA11yLabel: string;
  cashPriceLabel: string;
  callLabel: string;
}) {
  const capturePress = usePressCapture("PharmacyCard");
  const callButton = pharmacy.phone ? (
    <CompactActionButton
      label={pharmacy.phone}
      action="call_pharmacy"
      accessibilityLabel={`${callLabel} ${pharmacy.name} ${pharmacy.phone}`}
      onPress={(event) => {
        event.stopPropagation();
        void Linking.openURL(`tel:${pharmacy.phone?.replace(/[^\d+]/g, "")}`);
      }}
      variant="primary"
      size="sm"
      leadingIcon={Phone}
      iconStrokeWidth={1.8}
      style={{ alignSelf: "flex-start" }}
    />
  ) : null;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={detailsA11yLabel}
      onPress={() => {
        capturePress(pressLabel(pharmacy.name), "pharmacy_card");
        onPress();
      }}
      style={
        {
          flex: 1,
          width: "100%",
          minWidth: 0,
          gap: 8,
          padding: 16,
          borderRadius: 22,
          overflow: "hidden",
          backgroundColor: semanticColors.surface.subtle,
          borderWidth: 1,
          borderColor: semanticColors.border.glass,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.04,
          shadowRadius: 16,
          minHeight: 282,
          ...(Platform.OS === "web" && {
            backdropFilter: "blur(40px)",
            WebkitBackdropFilter: "blur(40px)",
          }),
        } as Record<string, unknown>
      }
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 46,
        }}
      >
        <PharmacyLogo pharmacy={pharmacy} />
        <ChevronRight
          accessibilityElementsHidden
          importantForAccessibility="no"
          size={18}
          color={EMBER.colors.warm}
          strokeWidth={1.8}
        />
      </View>

      <Text
        className="font-dm-sans"
        numberOfLines={2}
        style={{
          color: EMBER.colors.text,
          fontSize: 15,
          fontWeight: "700",
          lineHeight: 20,
        }}
      >
        {pharmacy.name}
      </Text>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        {pharmacy.specialties
          .filter(
            (specialty) =>
              ![
                "fertility",
                "cold-chain",
                "retail",
                "specialty",
                "mail_order",
                "same-day-delivery",
                "next-day-delivery",
                "overnight-delivery",
                "overnight-shipping",
                "in-store-pickup",
              ].includes(specialty),
          )
          .slice(0, 2)
          .map((specialty) => (
            <Tag
              key={specialty}
              label={PHARMACY_TAG_LABELS[specialty] ?? specialty}
              icon={PHARMACY_TAG_ICONS[specialty]}
              variant="outline"
              tone="neutral"
              style={PHARMACY_TAG_STYLE}
              textStyle={PHARMACY_TAG_TEXT_STYLE}
            />
          ))}
      </View>

      {demoMode && pharmacy.demo ? (
        <>
          <Tag
            label={pharmacy.demo.delivery}
            icon={Truck}
            variant="outline"
            tone="green"
            style={{
              ...PHARMACY_TAG_STYLE,
              alignSelf: "flex-start",
              marginTop: 2,
              backgroundColor: semanticColors.status.successTint,
              borderColor: withAlpha(semanticColors.status.success, 0.2),
            }}
            textStyle={{
              ...PHARMACY_TAG_TEXT_STYLE,
              color: semanticColors.status.success,
            }}
          />
          <View style={{ marginTop: "auto", paddingTop: 10, gap: 10 }}>
            <View>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textFaint,
                  fontSize: 9,
                  fontWeight: "700",
                  letterSpacing: 0.7,
                  textTransform: "uppercase",
                }}
              >
                {pharmacy.demo.totalPrice == null
                  ? contactForPricing
                  : cashPriceLabel}
              </Text>
              {pharmacy.demo.totalPrice != null ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.text,
                    fontSize: 18,
                    fontWeight: "800",
                    lineHeight: 23,
                  }}
                >
                  ${pharmacy.demo.totalPrice.toLocaleString("en-US")}
                </Text>
              ) : null}
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textFaint,
                  fontSize: 10,
                  marginTop: 2,
                }}
              >
                {pharmacy.demo.acceptsInsurance == null
                  ? coverageLabel
                  : pharmacy.demo.acceptsInsurance
                    ? coverageLabel
                    : cashPayLabel}
              </Text>
            </View>
            {callButton}
          </View>
        </>
      ) : (
        <View style={{ marginTop: "auto", paddingTop: 10, gap: 10 }}>
          <Text
            className="font-dm-sans"
            style={{ color: EMBER.colors.textLight, fontSize: 11 }}
          >
            {coverageLabel}
          </Text>
          {callButton}
        </View>
      )}
    </Pressable>
  );
}

export default function PharmacyScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const capturePress = usePressCapture("PharmacyScreen");
  const [demoMode, setDemoMode] = useState(() => {
    if (route.params?.demo === true) return true;
    if (Platform.OS !== "web" || typeof window === "undefined") return false;
    const demoParam = new URLSearchParams(window.location.search).get("demo");
    return demoParam === "1" || demoParam === "true";
  });
  // Web only: pointer events never fire on touch, where the toggle's
  // accessibility label and hint carry the same explanation.
  const [demoHintVisible, setDemoHintVisible] = useState(false);
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const rootNavigation =
    navigation.getParent<BottomTabNavigationProp<MainTabParamList>>();
  const {
    data: patientMeds,
    isLoading: medicationsLoading,
    isError: medicationsError,
    isFetching: medicationsFetching,
    refetch: refetchMedications,
  } = usePatientMedications();
  const {
    data: pharmacies,
    isLoading: pharmaciesLoading,
    error: pharmaciesError,
    isFetching: pharmaciesFetching,
    refetch: refetchPharmacies,
  } = usePharmacyDirectory({ demo: demoMode });
  const { data: documents = [] } = useDocuments();
  const { data: patient } = usePatient();
  const patientAddress = patient?.addresses?.[0];
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<PharmacyTab>("prescription");
  const [typeFilter, setTypeFilter] = useState<PharmacyDirectoryType | null>(
    null,
  );
  const [selectedFactsheet, setSelectedFactsheet] =
    useState<ContentBlock<"medication"> | null>(null);
  const [selectedUserContext, setSelectedUserContext] = useState<
    MedicationUserContext | undefined
  >(undefined);
  const [selectedSavingsProgram, setSelectedSavingsProgram] =
    useState<PharmacySavingsProgram | null>(null);
  const [selectedQuote, setSelectedQuote] =
    useState<SelectedPharmacyQuote | null>(null);
  const [selectedMedicationHelp, setSelectedMedicationHelp] =
    useState<MedicationHelpTopic | null>(null);
  const [quoteModalVisible, setQuoteModalVisible] = useState(false);
  const [conciergeSheetOpen, setConciergeSheetOpen] = useState(false);
  const [prescriptionUploadError, setPrescriptionUploadError] = useState<
    string | null
  >(null);
  const [confirmModal, setConfirmModal] = useState<null | {
    variant: ConfirmDialogVariant;
    title: string;
    body: string;
    cancelLabel: string;
    confirmLabel: string;
    onConfirm: () => void;
  }>(null);
  // Which pharmacy the "Upload quote" trailing button on a submission row
  // targets. Setting this opens `PharmacyQuoteCaptureModal` with the pharmacy
  // name pre-filled so we can attribute the upload without asking the patient.
  const [quoteModalInitialPharmacyName, setQuoteModalInitialPharmacyName] =
    useState<string>("");
  const [pendingPrescription, setPendingPrescription] =
    useState<PendingPrescription | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  // Request hidden with the ✕ on the Rx row. A draft has no delete endpoint
  // and a done request stays on record, so "start over" is local: the next
  // upload swaps the Rx onto the open draft (or starts a new request).
  const [dismissedQuoteRequestId, setDismissedQuoteRequestId] = useState<
    string | null
  >(null);
  // Bumped when the Rx is removed mid-upload so the in-flight pick stops
  // before it creates or updates a draft.
  const uploadGenerationRef = useRef(0);
  const documentTransfer = useDocumentSourceTransfer();

  const { data: currentQuoteRequest = null, refetch: refetchQuoteRequest } =
    useCurrentPharmacyQuoteRequest();
  const { mutateAsync: createQuoteDraft } =
    useCreatePharmacyQuoteRequestDraft();
  const { mutateAsync: updateQuoteDraft } =
    useUpdatePharmacyQuoteRequestDraft();
  const { mutateAsync: submitQuoteRequest } = useSubmitPharmacyQuoteRequest();
  const { mutate: cancelQuoteRequest } = useCancelPharmacyQuoteRequest();

  // Ops flip the per-pharmacy submissions from Slack; pick that up whenever
  // the patient comes back to this screen.
  useFocusEffect(
    useCallback(() => {
      void refetchQuoteRequest();
    }, [refetchQuoteRequest]),
  );

  const quoteRequest =
    currentQuoteRequest &&
    currentQuoteRequest.status !== "cancelled" &&
    currentQuoteRequest.id !== dismissedQuoteRequestId
      ? currentQuoteRequest
      : null;
  const quoteRequestStatus = toQuoteRequestStatus(quoteRequest);
  const prescriptionFile = pendingPrescription
    ? pendingPrescription.file
    : quoteRequest
      ? {
          name:
            quoteRequest.prescriptionFileName ??
            t("access.pharmacy.prescription_section_title"),
        }
      : null;
  const extractedMedications = useMemo<ExtractedMedication[]>(
    () =>
      pendingPrescription
        ? pendingPrescription.medications
        : (quoteRequest?.medicationsSnapshot ?? []).map((medication) => ({
            name: medication.name,
            dosage: medication.dosage ?? undefined,
          })),
    [pendingPrescription, quoteRequest],
  );
  const extractedClinicName =
    pendingPrescription?.clinicName ??
    quoteRequest?.clinicSnapshot?.clinicName ??
    "";
  const extractedPrescriberName =
    pendingPrescription?.prescriberName ??
    quoteRequest?.clinicSnapshot?.prescriberName ??
    "";
  const extractedStartDate =
    pendingPrescription?.startDate ??
    quoteRequest?.clinicSnapshot?.anticipatedStartDate ??
    "";
  const extractionFailed = pendingPrescription?.extractionFailed ?? false;

  const pickPrescriptionFile = useCallback(async () => {
    setPrescriptionUploadError(null);
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const asset = result.assets[0];
      const size = asset.size ?? 0;
      if (size > 10 * 1024 * 1024) {
        setPrescriptionUploadError(
          t("access.pharmacy.prescription_upload_error_size"),
        );
        return;
      }
      const mimeType = asset.mimeType ?? "application/octet-stream";
      const generation = ++uploadGenerationRef.current;
      const isCurrent = () => uploadGenerationRef.current === generation;
      setPendingPrescription({
        file: { uri: asset.uri, name: asset.name, mimeType, sizeBytes: size },
        medications: [],
        clinicName: "",
        prescriberName: "",
        startDate: "",
        extractionFailed: false,
      });
      setIsExtracting(true);

      let source: UploadedDocumentSource;
      try {
        source = await documentTransfer.uploadSource(
          { uri: asset.uri, name: asset.name, mimeType },
          {
            // Route through the canonical medication pipeline (same as
            // AddMedicationModal). The backend classifier resolves
            // form:"medication" to the medication handler, tags the
            // document, and can seed the patient's medication schedule.
            // We keep our own concierge `surface` for telemetry so we can
            // still filter this pathway in analytics.
            destination: "medication",
            sourceContext: {
              surface: "pharmacy_prescription_concierge_upload",
              route: "Pharmacy",
              form: "medication",
              targetHints: ["medication", "prescription"],
              allowBackgroundProcessing: false,
            },
          },
        );
      } catch (uploadError) {
        console.error("[pharmacy] Prescription upload failed", uploadError);
        if (!isCurrent()) return;
        setPendingPrescription(null);
        setIsExtracting(false);
        setPrescriptionUploadError(
          t("access.pharmacy.prescription_upload_error_file"),
        );
        return;
      }

      let documentProcessingRunId: string | null = null;
      let extracted: Omit<PendingPrescription, "file"> = {
        medications: [],
        clinicName: "",
        prescriberName: "",
        startDate: "",
        extractionFailed: false,
      };
      try {
        const outcome = await documentTransfer.processSource(source, {
          attempts: 40,
          intervalMs: 1500,
        });
        documentProcessingRunId = outcome.run.id;
        const prefill = projectDocumentProcessingRunToPrefill(
          outcome.run,
          asset.name,
        );
        extracted = {
          medications: (prefill.medications ?? [])
            .map((m) => ({
              name: m.medicationName?.trim() ?? "",
              dosage: [m.dosage, m.dosageUnit]
                .filter((v): v is string => Boolean(v && v.trim()))
                .join(" ")
                .trim(),
            }))
            .filter((m) => m.name.length > 0),
          clinicName: prefill.clinicName?.trim() ?? "",
          prescriberName: prefill.prescriber?.trim() ?? "",
          startDate: toIsoDate(prefill.documentDate?.trim() ?? "") ?? "",
          extractionFailed: false,
        };
      } catch (extractionError) {
        console.error(
          "[pharmacy] Prescription extraction failed",
          extractionError,
        );
        // The draft still goes out: a Louise operator reads the Rx by hand
        // before contacting pharmacies (see the detected-meds "failed" copy).
        extracted = { ...extracted, extractionFailed: true };
      }
      if (!isCurrent()) return;
      setPendingPrescription((current) =>
        current ? { ...current, ...extracted } : current,
      );
      setIsExtracting(false);

      // Anchor the request on the upload the backend now holds. The API keeps
      // one open draft per patient, so a Replace swaps the Rx on that draft.
      const snapshots = {
        documentProcessingRunId,
        medicationsSnapshot: extracted.medications.map((medication) => ({
          name: medication.name,
          dosage: medication.dosage || null,
        })),
        clinicSnapshot: {
          clinicName: extracted.clinicName || null,
          prescriberName: extracted.prescriberName || null,
          anticipatedStartDate: extracted.startDate || null,
        },
      };
      try {
        if (currentQuoteRequest?.status === "draft") {
          await updateQuoteDraft({
            id: currentQuoteRequest.id,
            body: { prescriptionUploadId: source.uploadId, ...snapshots },
          });
        } else {
          await createQuoteDraft({
            prescriptionUploadId: source.uploadId,
            ...snapshots,
          });
        }
        if (!isCurrent()) return;
        setDismissedQuoteRequestId(null);
        setPendingPrescription(null);
      } catch (draftError) {
        console.error("[pharmacy] Could not save the quote draft", draftError);
        if (!isCurrent()) return;
        setPendingPrescription(null);
        setPrescriptionUploadError(
          t("access.pharmacy.prescription_section_save_prescription_failed"),
        );
      }
    } catch (e) {
      setPrescriptionUploadError(
        t("access.pharmacy.prescription_upload_error_file"),
      );
    }
  }, [
    createQuoteDraft,
    currentQuoteRequest,
    documentTransfer,
    t,
    updateQuoteDraft,
  ]);

  const handleRemovePrescription = useCallback(() => {
    setPrescriptionUploadError(null);
    if (pendingPrescription) {
      uploadGenerationRef.current += 1;
      setPendingPrescription(null);
      setIsExtracting(false);
      documentTransfer.reset();
      return;
    }
    if (!quoteRequest) return;
    if (quoteRequest.status === "submitted") {
      const requestId = quoteRequest.id;
      setConfirmModal({
        variant: "destructive",
        title: t(
          "access.pharmacy.prescription_section_cancel_request_question",
        ),
        body: t(
          "access.pharmacy.prescription_section_cancel_request_consequence",
        ),
        cancelLabel: t("access.pharmacy.prescription_section_keep_request"),
        confirmLabel: t("access.pharmacy.prescription_section_cancel_request"),
        onConfirm: () =>
          cancelQuoteRequest(requestId, {
            onError: (error) =>
              setPrescriptionUploadError(
                getApiErrorMessage(
                  error,
                  t(
                    "access.pharmacy.prescription_section_cancel_request_failed",
                  ),
                ),
              ),
          }),
      });
      return;
    }
    setDismissedQuoteRequestId(quoteRequest.id);
    documentTransfer.reset();
  }, [
    cancelQuoteRequest,
    documentTransfer,
    pendingPrescription,
    quoteRequest,
    t,
  ]);

  const handleConciergeSubmit = useCallback(
    async (values: ConciergeQuoteFormValues) => {
      if (quoteRequest?.status !== "draft") {
        throw new Error(t("access.pharmacy.concierge_quote_submit_failed"));
      }
      // The backend records the consent (version + the exact text shown),
      // queues one submission per target pharmacy and posts the Slack card
      // ops work from. Pharmacies answer the patient's email directly.
      const submitted = await submitQuoteRequest({
        id: quoteRequest.id,
        body: {
          consent: {
            version: CONCIERGE_CONSENT_VERSION,
            locale,
            text: t("access.pharmacy.concierge_quote_consent"),
          },
          client: {
            appVersion: Constants.expoConfig?.version ?? null,
            platform: Platform.OS,
          },
          patientSnapshot: {
            firstName: values.firstName || null,
            lastName: values.lastName || null,
            dateOfBirth: toIsoDate(values.dateOfBirth),
            email: values.email,
            phone: values.phone || null,
            address: values.addressLine1 || null,
            city: values.city || null,
            state: values.state || null,
            zip: values.zipCode || null,
            country: values.country || null,
          },
          clinicSnapshot: {
            clinicName: values.clinicName || null,
            prescriberName: values.prescriberName || null,
            cycleType: values.cycleType || null,
            anticipatedStartDate: toIsoDate(values.anticipatedStartDate),
            military: values.isMilitary,
          },
        },
      });
      // Confirm the send and set the expectation for the new model: pharmacies
      // reply straight to the patient's email inbox, not inside the app.
      Alert.alert(
        t("access.pharmacy.concierge_quote_success_title"),
        t("access.pharmacy.concierge_quote_success_body", {
          params: { count: String(submitted.totalCount) },
        }),
        [{ text: t("access.pharmacy.concierge_quote_success_ok") }],
      );
      // Move the patient over to the Quotes tab so they see the per-pharmacy
      // progress + "upload quotes when they arrive" state instead of the Rx.
      setActiveTab("quotes");
    },
    [locale, quoteRequest, submitQuoteRequest, t],
  );

  const handleOpenQuotesTab = useCallback(() => {
    setActiveTab("quotes");
  }, []);

  // Open the "Add price / upload quote" modal seeded with a pharmacy name.
  // Passing "" opens it blank so the patient can enter any pharmacy — used by
  // the "Upload from another pharmacy" affordance for quotes from pharmacies
  // not on Louise's concierge send list.
  const openQuoteCaptureFor = useCallback((pharmacyName: string) => {
    setQuoteModalInitialPharmacyName(pharmacyName);
    setQuoteModalVisible(true);
  }, []);

  const patientQuotes = useMemo(() => {
    if (demoMode) return [];

    return documents
      .map(pharmacyQuoteFromDocument)
      .filter((quote): quote is PharmacyPatientQuote => quote !== null)
      .sort((a, b) => b.quoteDate.localeCompare(a.quoteDate));
  }, [demoMode, documents]);

  const openMedicationFlow = useCallback(() => {
    rootNavigation?.navigate("Care", {
      screen: "CareOverview",
      params: { focus: "medication" },
    });
  }, [rootNavigation]);

  const displayMeds = useMemo<DisplayMedication[]>(() => {
    if (demoMode) return MOCK_PHARMACY_MEDICATIONS;

    return (patientMeds ?? [])
      .filter(
        (medication) =>
          medication.medication?.name || medication.rawMedicationName,
      )
      .map((medication) => ({
        id: medication.medicationId ?? medication.id,
        name: medication.medication?.name ?? medication.rawMedicationName ?? "",
        dosage: medication.dose
          ? `${medication.dose} ${medication.doseUnit ?? ""}`.trim()
          : "",
        form: getMedicationForm(medication.medication?.intakeType),
      }));
  }, [demoMode, patientMeds]);

  const bestMedicationPrices = useMemo(() => {
    const result: Record<
      string,
      { price: number; pharmacy: PharmacyDirectoryRecord }
    > = {};
    if (!demoMode) return result;

    displayMeds.forEach((medication) => {
      const best = pharmacies
        .map((pharmacy) => ({
          pharmacy,
          price: getPharmacyMedicationPrice(pharmacy, medication.id),
        }))
        .filter(
          (
            candidate,
          ): candidate is {
            pharmacy: PharmacyDirectoryRecord;
            price: number;
          } => candidate.price != null,
        )
        .sort((a, b) => a.price - b.price)[0];

      if (best) result[medication.id] = best;
    });

    return result;
  }, [demoMode, displayMeds, pharmacies]);

  const handleMedPress = useCallback((medication: DisplayMedication) => {
    const factsheet = getMedicationByBrandName(medication.name) ?? null;
    if (!factsheet) return;
    setSelectedFactsheet(factsheet);
    setSelectedUserContext({
      dosage: medication.dosage,
      route: medication.route ?? null,
    });
  }, []);

  const handleFulfillmentMedicationPress = useCallback(
    (payload: HomepageMedicationFactSheetPayload) => {
      setSelectedFactsheet(payload.item);
      setSelectedUserContext(payload.userContext);
    },
    [],
  );

  const filteredPharmacies = useMemo(() => {
    const query = search.trim().toLowerCase();
    return pharmacies.filter((pharmacy) => {
      const matchesSearch =
        !query ||
        pharmacy.name.toLowerCase().includes(query) ||
        pharmacy.city?.toLowerCase().includes(query) ||
        pharmacy.state?.toLowerCase().includes(query);
      const matchesType = !typeFilter || pharmacy.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [pharmacies, search, typeFilter]);

  const rows = useMemo(() => {
    const result: PharmacyDirectoryRecord[][] = [];
    for (let index = 0; index < filteredPharmacies.length; index += 2) {
      result.push(filteredPharmacies.slice(index, index + 2));
    }
    return result;
  }, [filteredPharmacies]);

  const quoteRows = useMemo(() => {
    const cards = MOCK_PHARMACY_QUOTES.reduce<
      { quote: PharmacyDemoQuote; pharmacy: PharmacyDirectoryRecord }[]
    >((result, quote) => {
      const pharmacy = pharmacies.find(
        (candidate) => candidate.id === quote.pharmacyId,
      );
      if (pharmacy) result.push({ quote, pharmacy });
      return result;
    }, []);
    const result: (typeof cards)[] = [];
    for (let index = 0; index < cards.length; index += 2) {
      result.push(cards.slice(index, index + 2));
    }
    return result;
  }, [pharmacies]);

  const localizedSavingsPrograms = useMemo(
    () =>
      PHARMACY_SAVINGS_PROGRAMS.map((program) =>
        localizePharmacySavingsProgram(program, locale),
      ),
    [locale],
  );
  const savingsProgramCount = localizedSavingsPrograms.length;

  const filterOptions: {
    key: PharmacyDirectoryType | null;
    label: string;
    icon: LucideIcon;
  }[] = [
    { key: null, label: t("access.pharmacy.filter_all"), icon: Layers },
    {
      key: "specialty",
      label: t("access.pharmacy.filter_specialty"),
      icon: Building2,
    },
    {
      key: "mail_order",
      label: t("access.pharmacy.filter_mail_order"),
      icon: Mail,
    },
    { key: "retail", label: t("access.pharmacy.filter_retail"), icon: Store },
  ];

  const quoteActions = (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 8,
      }}
    >
      <CompactActionButton
        leadingIcon={UploadCloud}
        iconSize={14}
        iconStrokeWidth={1.8}
        label={t("access.pharmacy.quote_upload")}
        action="access.pharmacy.quote_upload"
        onPress={() => setQuoteModalVisible(true)}
      />
      <CompactActionButton
        leadingIcon={DollarSign}
        iconSize={14}
        iconStrokeWidth={1.8}
        label={t("access.pharmacy.quote_add_price")}
        action="access.pharmacy.quote_add_price"
        onPress={() => setQuoteModalVisible(true)}
        variant="secondary"
      />
    </View>
  );

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          width: "100%",
          maxWidth: 520,
          alignSelf: "center",
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
        }}
        showsVerticalScrollIndicator={false}
      >
        <BackLink
          label={
            route.params?.returnTab ? t("you.shared.back") : t("access.back")
          }
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        <PharmacyHeader
          rightAccessory={
            // Hover is read on the wrapper so the sparkles explain the toggle
            // too: on their own they say nothing about what switches.
            <View
              style={{
                position: "relative",
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
              }}
              onPointerEnter={() => setDemoHintVisible(true)}
              onPointerLeave={() => setDemoHintVisible(false)}
            >
              <Sparkles size={16} color={EMBER.colors.warm} strokeWidth={2} />
              <MoonToggle
                value={demoMode}
                onChange={(next) => {
                  setDemoMode(next);
                  setSearch("");
                  setTypeFilter(null);
                }}
                testID="pharmacy-demo-toggle"
                accessibilityLabel={t("access.pharmacy.demo_mode_title")}
                accessibilityHint={t("access.pharmacy.demo_mode_hint")}
              />
              {demoHintVisible ? (
                <View
                  // Not hit-testable: a label that could be hovered would
                  // flicker as the pointer crossed onto it.
                  pointerEvents="none"
                  style={{
                    position: "absolute",
                    bottom: 32,
                    right: 0,
                    width: 240,
                    paddingHorizontal: 10,
                    paddingVertical: 8,
                    borderRadius: 10,
                    backgroundColor: "rgba(57,36,29,0.94)",
                    gap: 2,
                  }}
                >
                  <Text
                    className="font-dm-sans-semibold"
                    style={{
                      fontSize: 12,
                      lineHeight: 16,
                      color: semanticColors.onDark.text.primary,
                    }}
                  >
                    {t("access.pharmacy.demo_mode_title")}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      lineHeight: 16,
                      color: semanticColors.onDark.text.primary,
                    }}
                  >
                    {t("access.pharmacy.demo_mode_body")}
                  </Text>
                </View>
              ) : null}
            </View>
          }
        />

        <CostsCardTabs
          active={activeTab}
          onChange={setActiveTab}
          options={[
            {
              value: "prescription",
              label: t("access.pharmacy.tab_prescription"),
              icon: Pill,
            },
            {
              value: "quotes",
              label: t("access.pharmacy.tab_quotes"),
              icon: DollarSign,
            },
            {
              value: "savings",
              label: t("access.pharmacy.tab_savings"),
              icon: HeartHandshake,
            },
          ]}
          style={{ marginTop: 24 }}
        />

        {activeTab === "prescription" ? (
          <>
            <PharmacyPrescriptionSection
              file={prescriptionFile}
              extractedMedications={extractedMedications}
              isExtracting={isExtracting}
              extractionFailed={extractionFailed}
              quoteRequestStatus={quoteRequestStatus}
              requestQuotesDisabled={quoteRequest?.status !== "draft"}
              onUploadPress={pickPrescriptionFile}
              onReplacePress={pickPrescriptionFile}
              onRemovePress={handleRemovePrescription}
              onRequestQuotesPress={() => setConciergeSheetOpen(true)}
              onOpenQuotesTabPress={handleOpenQuotesTab}
              titleLabel={t("access.pharmacy.prescription_section_title")}
              emptyBodyLabel={t(
                "access.pharmacy.prescription_section_empty_body",
              )}
              uploadCtaLabel={t(
                "access.pharmacy.prescription_section_upload_cta",
              )}
              filledBodyLabel={t(
                "access.pharmacy.prescription_section_filled_body",
              )}
              detectedMedsEmpty={t(
                "access.pharmacy.prescription_section_detected_meds_empty",
              )}
              detectedMedsProcessing={t(
                "access.pharmacy.prescription_section_detected_meds_processing",
              )}
              detectedMedsFailed={t(
                "access.pharmacy.prescription_section_detected_meds_failed",
              )}
              replaceLabel={t("access.pharmacy.prescription_section_replace")}
              removeLabel={t("access.pharmacy.prescription_section_remove")}
              requestQuotesLabel={t(
                "access.pharmacy.prescription_section_request_quotes",
              )}
              inProgressTitle={t(
                "access.pharmacy.prescription_section_in_progress_title",
              )}
              inProgressBody={t(
                "access.pharmacy.prescription_section_in_progress_body",
              )}
              inProgressCta={t(
                "access.pharmacy.prescription_section_in_progress_cta",
              )}
              doneTitle={t(
                "access.pharmacy.prescription_section_all_requests_sent",
              )}
              doneBody={t(
                "access.pharmacy.prescription_section_quotes_arrive_by_email",
              )}
            />
            {prescriptionUploadError ? (
              <View style={{ marginTop: -8, marginBottom: 12 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    color: "#B84A2E",
                    fontSize: 12,
                  }}
                  accessibilityRole="alert"
                >
                  {prescriptionUploadError}
                </Text>
              </View>
            ) : null}

            <View
              style={{
                ...(GLASS_CARD_STYLE as object),
                marginTop: 0,
                padding: 0,
                paddingBottom: displayMeds.length === 0 ? 0 : 12,
                overflow: "hidden",
              }}
            >
              {displayMeds.length > 0 ||
              (!demoMode && (medicationsLoading || medicationsError)) ? (
                <View
                  style={{
                    paddingHorizontal: 18,
                    paddingTop: 18,
                    paddingBottom: 12,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.text,
                      fontSize: 17,
                      fontWeight: "700",
                    }}
                  >
                    {t("access.pharmacy.section_meds_title")}
                  </Text>
                </View>
              ) : null}

              {!demoMode && medicationsLoading ? (
                <View
                  accessibilityRole="progressbar"
                  accessibilityLabel={t("access.pharmacy.meds_loading_title")}
                  style={{ paddingHorizontal: 4, paddingBottom: 8 }}
                >
                  {[0, 1].map((row) => (
                    <ListRowSkeleton key={row} showLeading showSubtitle />
                  ))}
                </View>
              ) : !demoMode && medicationsError ? (
                <ClinicalDataStatus
                  state="unavailable"
                  title={t("access.pharmacy.meds_error_title")}
                  message={t("access.pharmacy.meds_error_body")}
                  retryLabel={t("clinical_data.retry")}
                  retryingLabel={t("clinical_data.retrying")}
                  onRetry={() => void refetchMedications()}
                  isRetrying={medicationsFetching}
                  embedded
                  testID="pharmacy-medications-unavailable"
                />
              ) : displayMeds.length === 0 ? (
                <View
                  style={{
                    paddingHorizontal: 18,
                    paddingTop: 16,
                    paddingBottom: 6,
                  }}
                >
                  <EmptyState
                    density="compact"
                    title={t("empty_states.no_medications_connected")}
                    subtitle={t("access.pharmacy.meds_empty_body")}
                    style={{ paddingVertical: 0, paddingHorizontal: 0 }}
                    illustration={
                      <Image
                        source={pillCapsuleImage}
                        resizeMode="contain"
                        style={{ width: 86, height: 60 }}
                      />
                    }
                    action={
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        <CompactActionButton
                          leadingIcon={Pill}
                          iconSize={14}
                          iconStrokeWidth={1.8}
                          label={t("access.pharmacy.meds_add_medication")}
                          action="access.pharmacy.meds_add_medication"
                          onPress={openMedicationFlow}
                        />
                        <CompactActionButton
                          leadingIcon={UploadCloud}
                          iconSize={14}
                          iconStrokeWidth={1.8}
                          label={t("access.pharmacy.meds_upload_prescription")}
                          action="access.pharmacy.meds_upload_prescription"
                          onPress={openMedicationFlow}
                          variant="secondary"
                        />
                      </View>
                    }
                  />
                </View>
              ) : (
                displayMeds.map((medication) => (
                  <Pressable
                    key={medication.id}
                    accessibilityRole="button"
                    accessibilityLabel={t(
                      "access.pharmacy.medication_guide_a11y",
                      {
                        params: { name: medication.name },
                      },
                    )}
                    onPress={() => {
                      capturePress(
                        pressLabel(medication.name),
                        "medication_row",
                      );
                      handleMedPress(medication);
                    }}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      paddingHorizontal: 16,
                      paddingVertical: 10,
                    }}
                  >
                    <MedFormIcon form={medication.form} />
                    <View style={{ flex: 1 }}>
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.text,
                          fontSize: 14,
                          fontWeight: "500",
                        }}
                      >
                        {medication.name}
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 2,
                        }}
                      >
                        {medication.dosage ? (
                          <Text
                            className="font-dm-sans"
                            style={{
                              color: EMBER.colors.textLight,
                              fontSize: 12,
                            }}
                          >
                            {medication.dosage}
                          </Text>
                        ) : null}
                        {demoMode && medication.route ? (
                          <Text
                            className="font-dm-sans"
                            style={{
                              color: EMBER.colors.textFaint,
                              fontSize: 11,
                            }}
                          >
                            · {medication.route}
                          </Text>
                        ) : null}
                        {demoMode && medication.coverage ? (
                          <View
                            style={{
                              paddingHorizontal: 7,
                              paddingVertical: 2,
                              borderRadius: 999,
                              backgroundColor:
                                medication.coverage === "covered"
                                  ? semanticColors.status.successTint
                                  : BROWN_BG,
                            }}
                          >
                            <Text
                              className="font-dm-sans"
                              style={{
                                color:
                                  medication.coverage === "covered"
                                    ? EMBER.colors.green
                                    : BROWN,
                                fontSize: 9,
                                fontWeight: "700",
                              }}
                            >
                              {t(
                                medication.coverage === "covered"
                                  ? "access.pharmacy.coverage.covered"
                                  : "access.pharmacy.coverage.cash_pay",
                              )}
                            </Text>
                          </View>
                        ) : null}
                      </View>
                    </View>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {(() => {
                        const bestPrice = bestMedicationPrices[medication.id];
                        return (
                          <>
                            {bestPrice &&
                            pharmacyLogoImages[bestPrice.pharmacy.id] ? (
                              <Image
                                source={
                                  pharmacyLogoImages[bestPrice.pharmacy.id]
                                }
                                resizeMode="contain"
                                style={{ width: 44, height: 17 }}
                              />
                            ) : null}
                            {bestPrice ? (
                              <Text
                                className="font-dm-sans"
                                style={{
                                  color: EMBER.colors.text,
                                  fontSize: 12,
                                  fontWeight: "700",
                                }}
                              >
                                {`$${bestPrice.price.toLocaleString("en-US")}`}
                              </Text>
                            ) : null}
                            <ChevronRight
                              size={16}
                              color={EMBER.colors.textFaint}
                              strokeWidth={1.5}
                            />
                          </>
                        );
                      })()}
                    </View>
                  </Pressable>
                ))
              )}
            </View>

            {demoMode ? (
              <View style={{ marginTop: 28 }}>
                <View style={{ marginBottom: 12 }}>
                  <SectionTitle icon={Truck}>
                    {t("access.pharmacy.fulfillment_section_eyebrow")}
                  </SectionTitle>
                </View>
                <View style={{ gap: 10 }}>
                  {pharmacyFulfillmentCards.map((card) =>
                    card.kind === "refill-action" ||
                    card.kind === "pickup-action" ? (
                      <PharmacyActionCard
                        key={card.id}
                        card={card}
                        onOpenMedicationFactSheet={
                          handleFulfillmentMedicationPress
                        }
                      />
                    ) : (
                      <DesignedPharmacyFulfillmentCard
                        key={card.id}
                        card={card}
                        onOpenMedicationFactSheet={
                          handleFulfillmentMedicationPress
                        }
                      />
                    ),
                  )}
                </View>
              </View>
            ) : null}

            <View style={{ marginTop: 28 }}>
              <View style={{ marginBottom: 12 }}>
                <SectionTitle icon={Shield}>
                  {t("access.pharmacy.medication_help_section_eyebrow")}
                </SectionTitle>
              </View>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 10, paddingRight: 16 }}
                style={{ marginHorizontal: -16, paddingLeft: 16 }}
              >
                {MEDICATION_HELP_TOPICS.map((topic) => (
                  <MedicationHelpCard
                    key={topic.id}
                    topic={topic}
                    onPress={() => setSelectedMedicationHelp(topic)}
                  />
                ))}
              </ScrollView>
            </View>
          </>
        ) : null}

        {activeTab === "quotes" ? (
          <PharmacyQuotesTabView
            quoteRequest={quoteRequest}
            patientQuotes={patientQuotes}
            onOpenPatientQuote={(quote) =>
              setSelectedQuote({ kind: "patient", quote })
            }
            onOpenUploadFor={openQuoteCaptureFor}
          />
        ) : null}

        {activeTab === "savings" ? (
          <View
            style={{
              ...(GLASS_CARD_STYLE as object),
              paddingTop: 16,
              paddingLeft: 16,
              paddingBottom: 16,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                paddingRight: 16,
                paddingBottom: 14,
                gap: 4,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.text,
                  fontSize: 16,
                  fontWeight: "700",
                }}
              >
                {t("access.pharmacy.savings_summary_title", {
                  params: { count: String(savingsProgramCount) },
                })}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textLight,
                  fontSize: 12,
                  lineHeight: 18,
                }}
              >
                {t("access.pharmacy.savings_summary_body")}
              </Text>
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 10, paddingRight: 16 }}
            >
              {localizedSavingsPrograms.map((program) => (
                <PharmacySavingsProgramCard
                  key={program.id}
                  program={program}
                  onPress={() => setSelectedSavingsProgram(program)}
                />
              ))}
            </ScrollView>
          </View>
        ) : null}

        <View style={{ marginTop: 28 }}>
          <View style={{ marginBottom: 12 }}>
            <SectionTitle icon={Store}>
              {t("access.pharmacy.section_directory_eyebrow")}
            </SectionTitle>
          </View>

          <MoonSearchInput
            placeholder={t("access.pharmacy.search_placeholder")}
            value={search}
            onChangeText={setSearch}
            onClear={() => setSearch("")}
            tone="glass"
            style={{ marginBottom: 14 }}
          />

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 8, paddingBottom: 14 }}
          >
            {filterOptions.map((option) => (
              <MoonFilterChip
                key={option.label}
                label={option.label}
                icon={option.icon}
                selected={typeFilter === option.key}
                onPress={() => setTypeFilter(option.key)}
                tone="glass"
                showCheck={false}
              />
            ))}
          </ScrollView>

          {pharmaciesLoading ? (
            <View
              accessibilityRole="progressbar"
              accessibilityLabel={t("access.pharmacy.directory_loading_title")}
            >
              {[0, 1, 2].map((row) => (
                <ListRowSkeleton key={row} showLeadingSquare showSubtitle />
              ))}
            </View>
          ) : pharmaciesError && pharmacies.length === 0 ? (
            <ClinicalDataStatus
              state="unavailable"
              title={t("access.pharmacy.directory_error_title")}
              message={t("states.check_connection_and_try_again")}
              retryLabel={t("clinical_data.retry")}
              retryingLabel={t("clinical_data.retrying")}
              onRetry={refetchPharmacies}
              isRetrying={pharmaciesFetching}
              testID="pharmacy-directory-unavailable"
            />
          ) : (
            <>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textFaint,
                  fontSize: 13,
                  marginBottom: 14,
                }}
              >
                {t(
                  filteredPharmacies.length === 1
                    ? "access.pharmacy.result_count_one"
                    : "access.pharmacy.result_count_other",
                  { params: { count: String(filteredPharmacies.length) } },
                )}
              </Text>
              <View style={{ gap: 12 }}>
                {rows.map((row, rowIndex) => (
                  <View
                    key={rowIndex}
                    style={{
                      width: "100%",
                      flexDirection: "row",
                      gap: 12,
                    }}
                  >
                    {row.map((pharmacy) => (
                      <View key={pharmacy.id} style={{ flex: 1, minWidth: 0 }}>
                        <PharmacyCard
                          pharmacy={pharmacy}
                          coverageLabel={t("access.pharmacy.coverage_varies")}
                          cashPayLabel={t("access.pharmacy.cash_pay_only")}
                          contactForPricing={t(
                            "access.pharmacy.contact_for_pricing",
                          )}
                          demoMode={demoMode}
                          detailsA11yLabel={t(
                            "access.pharmacy.view_details_a11y",
                            { params: { name: pharmacy.name } },
                          )}
                          cashPriceLabel={t("access.pharmacy.cash_price_label")}
                          callLabel={t("access.pharmacy.call_pharmacy")}
                          onPress={() =>
                            navigation.navigate("PharmacyDetail", {
                              pharmacyId: pharmacy.id,
                              demo: demoMode,
                            })
                          }
                        />
                      </View>
                    ))}
                    {row.length === 1 ? (
                      <View style={{ flex: 1, minWidth: 0 }} />
                    ) : null}
                  </View>
                ))}
              </View>
              {filteredPharmacies.length === 0 ? (
                <SearchEmptyState
                  title={t("access.pharmacy.empty")}
                  onReset={() => {
                    setSearch("");
                  }}
                />
              ) : null}
            </>
          )}
        </View>
      </ScrollView>

      <MedicationFactsheetSheet
        item={selectedFactsheet}
        onClose={() => {
          setSelectedFactsheet(null);
          setSelectedUserContext(undefined);
        }}
        userContext={selectedUserContext}
      />

      <SavingsProgramDialog
        program={selectedSavingsProgram}
        onClose={() => setSelectedSavingsProgram(null)}
      />
      <MedicationHelpDialog
        topic={selectedMedicationHelp}
        onClose={() => setSelectedMedicationHelp(null)}
        medications={displayMeds}
      />
      <PharmacyQuoteCaptureModal
        visible={quoteModalVisible}
        onClose={() => {
          setQuoteModalVisible(false);
          setQuoteModalInitialPharmacyName("");
        }}
        initialPharmacyName={quoteModalInitialPharmacyName}
      />
      <PharmacyQuoteDetailDialog
        selection={selectedQuote}
        onClose={() => setSelectedQuote(null)}
      />
      <PharmacyConciergeQuoteSheet
        visible={conciergeSheetOpen}
        onClose={() => setConciergeSheetOpen(false)}
        file={prescriptionFile}
        onSubmit={handleConciergeSubmit}
        initialFirstName={patient?.firstName ?? ""}
        initialLastName={patient?.lastName ?? ""}
        initialDateOfBirth={patient?.dateOfBirth ?? ""}
        initialEmail={patient?.email ?? ""}
        initialPhone={patient?.phoneNumber ?? ""}
        initialAddressLine1={patientAddress?.address1 ?? ""}
        initialCity={patientAddress?.city ?? ""}
        initialZipCode={patientAddress?.postalCode ?? ""}
        initialCountry={patientAddress?.country ?? ""}
        initialClinicName={extractedClinicName}
        initialPrescriberName={extractedPrescriberName}
        initialAnticipatedStartDate={extractedStartDate}
        titleLabel={t("access.pharmacy.concierge_quote_eyebrow")}
        step1TitleLabel={t("access.pharmacy.concierge_quote_step1_title")}
        step1BodyLabel={t("access.pharmacy.concierge_quote_step1_body")}
        step2TitleLabel={t("access.pharmacy.concierge_quote_step2_title")}
        step2BodyLabel={t("access.pharmacy.concierge_quote_step2_body")}
        step3TitleLabel={t("access.pharmacy.concierge_quote_step3_title")}
        step3BodyLabel={t("access.pharmacy.concierge_quote_step3_body")}
        firstNameLabel={t("access.pharmacy.concierge_quote_first_name_label")}
        firstNamePlaceholder={t(
          "access.pharmacy.concierge_quote_first_name_placeholder",
        )}
        lastNameLabel={t("access.pharmacy.concierge_quote_last_name_label")}
        lastNamePlaceholder={t(
          "access.pharmacy.concierge_quote_last_name_placeholder",
        )}
        dobLabel={t("access.pharmacy.concierge_quote_dob_label")}
        emailLabel={t("access.pharmacy.concierge_quote_email_label")}
        emailPlaceholder={t(
          "access.pharmacy.concierge_quote_email_placeholder",
        )}
        phoneLabel={t("access.pharmacy.concierge_quote_phone_label")}
        addressLabel={t("access.pharmacy.concierge_quote_address_label")}
        addressPlaceholder={t(
          "access.pharmacy.concierge_quote_address_placeholder",
        )}
        clinicNameLabel={t("access.pharmacy.concierge_quote_clinic_label")}
        clinicNamePlaceholder={t(
          "access.pharmacy.concierge_quote_clinic_placeholder",
        )}
        prescriberNameLabel={t(
          "access.pharmacy.concierge_quote_prescriber_label",
        )}
        prescriberNamePlaceholder={t(
          "access.pharmacy.concierge_quote_prescriber_placeholder",
        )}
        cycleTypeLabel={t("access.pharmacy.concierge_quote_cycle_label")}
        cycleTypeOptions={[
          { id: "ivf", label: t("access.pharmacy.concierge_quote_cycle_ivf") },
          { id: "iui", label: t("access.pharmacy.concierge_quote_cycle_iui") },
          { id: "fet", label: t("access.pharmacy.concierge_quote_cycle_fet") },
          {
            id: "egg_freezing",
            label: t("access.pharmacy.concierge_quote_cycle_egg_freezing"),
          },
          {
            id: "timed_intercourse",
            label: t("access.pharmacy.concierge_quote_cycle_timed_intercourse"),
          },
          {
            id: "donor_egg_ip",
            label: t("access.pharmacy.concierge_quote_cycle_donor_egg_ip"),
          },
          {
            id: "donor_egg_donor",
            label: t("access.pharmacy.concierge_quote_cycle_donor_egg_donor"),
          },
          {
            id: "gc_ip",
            label: t("access.pharmacy.concierge_quote_cycle_gc_ip"),
          },
          {
            id: "gc_carrier",
            label: t("access.pharmacy.concierge_quote_cycle_gc_carrier"),
          },
          {
            id: "other",
            label: t("access.pharmacy.concierge_quote_cycle_other"),
          },
        ]}
        anticipatedStartLabel={t(
          "access.pharmacy.concierge_quote_start_date_label",
        )}
        militaryLabel={t("access.pharmacy.concierge_quote_military_label")}
        consentLabel={t("access.pharmacy.concierge_quote_consent")}
        submitFailedError={t("access.pharmacy.concierge_quote_submit_failed")}
        submitLabel={t("access.pharmacy.concierge_quote_submit")}
        continueLabel={t("access.pharmacy.concierge_quote_continue")}
        backLabel={t("access.pharmacy.concierge_quote_back")}
        closeLabel={t("access.pharmacy.concierge_quote_close")}
        missingFieldsPrefixLabel={t(
          "access.pharmacy.concierge_quote_missing_fields_prefix",
        )}
        step3RequiredError={t("access.pharmacy.concierge_quote_step3_error")}
      />
      <PharmacyConciergeConfirmModal
        visible={confirmModal !== null}
        variant={confirmModal?.variant ?? "handoff"}
        title={confirmModal?.title ?? ""}
        body={confirmModal?.body ?? ""}
        cancelLabel={confirmModal?.cancelLabel ?? ""}
        confirmLabel={confirmModal?.confirmLabel ?? ""}
        closeLabel={t("access.pharmacy.concierge_quote_close")}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => {
          const onConfirm = confirmModal?.onConfirm;
          setConfirmModal(null);
          onConfirm?.();
        }}
      />
    </GlassScreenLayout>
  );
}
